/* ==========
   ALLUREIS — SNAPMAGS SHOPIFY THEME JS
   Vanilla JS only. No frameworks. Mobile-first. iPhone-native feel.
   Modules (IIFEs) in order:
     1. Utils
     2. Toast
     3. Header + Mobile Nav
     4. Cart (Shopify AJAX API)
     5. Cart Drawer
     6. Email Popup
     7. Before/After slider
     8. FAQ Accordion
     9. Product Page (variant logic, bundle, sticky ATC, urgency)
    10. DOMReady init
   ========== */

'use strict';

const $ = (sel, ctx = document) => ctx.querySelector(sel);
const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

// Shared scroll-lock helpers — every drawer/popup (cart drawer, email popup,
// bundle upsell popup, mobile nav) locks body scroll the same way. Setting
// overflow:hidden on body removes the scrollbar, which shrinks the viewport
// width and shifts all fixed/centered content left by the scrollbar's width
// (~15-17px desktop). Compensating with padding-right holds everything in
// place. Reference-counted so nested opens (e.g. popup opened while nav is
// open) don't unlock prematurely.
let __scrollLockCount = 0;
function lockBodyScroll() {
  if (__scrollLockCount === 0) {
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) document.body.style.paddingRight = `${scrollbarWidth}px`;
  }
  __scrollLockCount++;
}
function unlockBodyScroll() {
  __scrollLockCount = Math.max(0, __scrollLockCount - 1);
  if (__scrollLockCount === 0) {
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
  }
}

function formatMoney(cents, currency = 'USD') {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency, minimumFractionDigits: 2 }).format(cents / 100);
}

const Toast = (() => {
  let container;
  function getContainer() {
    if (!container) {
      container = document.getElementById('toast-container');
      if (!container) { container = document.createElement('div'); container.id = 'toast-container'; document.body.appendChild(container); }
    }
    return container;
  }
  function show(message, duration = 3000) {
    const c = getContainer();
    const el = document.createElement('div');
    el.className = 'toast';
    el.textContent = message;
    c.appendChild(el);
    setTimeout(() => { el.style.opacity = '0'; el.style.transition = 'opacity 0.3s'; setTimeout(() => el.remove(), 300); }, duration);
  }
  return { show };
})();

const Header = (() => {
  let header, hamburger, mobileNav, lastScrollY, ticking;
  function init() {
    header = document.getElementById('site-header');
    hamburger = document.getElementById('hamburger-btn');
    mobileNav = document.getElementById('mobile-nav');
    if (!header) return;
    lastScrollY = window.scrollY;
    const isHome = document.body.classList.contains('is-home');
    if (!isHome) header.classList.add('is-visible');
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    if (hamburger) hamburger.addEventListener('click', toggleMobileNav);
    if (mobileNav) {
      $$('a', mobileNav).forEach(a => a.addEventListener('click', closeMobileNav));
      const closeBtn = document.getElementById('mobile-nav-close');
      if (closeBtn) closeBtn.addEventListener('click', closeMobileNav);
    }
  }
  function onScroll() { if (ticking) return; ticking = true; requestAnimationFrame(update); }
  function update() {
    ticking = false;
    const y = window.scrollY;
    const isHome = document.body.classList.contains('is-home');
    if (isHome) {
      if (y > 1) { header.classList.add('is-visible'); header.classList.remove('is-hidden'); }
      else header.classList.remove('is-visible');
    } else {
      header.classList.add('is-visible');
      if (y > lastScrollY && y > 120) header.classList.add('is-hidden');
      else header.classList.remove('is-hidden');
    }
    lastScrollY = y;
  }
  function toggleMobileNav() { const isOpen = mobileNav && mobileNav.classList.contains('is-open'); if (isOpen) closeMobileNav(); else openMobileNav(); }
  function openMobileNav() { if (!mobileNav) return; mobileNav.classList.add('is-open'); if (hamburger) hamburger.classList.add('is-open'); lockBodyScroll(); }
  function closeMobileNav() { if (!mobileNav) return; mobileNav.classList.remove('is-open'); if (hamburger) hamburger.classList.remove('is-open'); unlockBodyScroll(); }
  return { init };
})();

const Cart = (() => {
  let _state = { items: [], item_count: 0, total_price: 0, currency: 'USD' };
  const listeners = [];
  function subscribe(fn) { listeners.push(fn); }
  function notify() { listeners.forEach(fn => fn(_state)); }
  async function fetch() {
    try { const r = await window.fetch('/cart.js'); _state = await r.json(); notify(); return _state; }
    catch (e) { console.error('[Cart] fetch failed', e); return _state; }
  }
  async function addItem({ variantId, quantity = 1, properties = {} }) {
    const body = JSON.stringify({ id: variantId, quantity, properties });
    const r = await window.fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body });
    if (!r.ok) { const err = await r.json().catch(() => ({})); throw new Error(err.description || 'Could not add item'); }
    await fetch();
    return _state;
  }
  async function addItems(items) { for (const item of items) { await addItem(item); } return _state; }
  async function changeItem({ key, quantity }) {
    const r = await window.fetch('/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: key, quantity }) });
    if (!r.ok) throw new Error('Cart change failed');
    _state = await r.json(); notify(); return _state;
  }
  async function removeItem(key) { return changeItem({ key, quantity: 0 }); }
  function getState() { return _state; }
  async function goToCheckout() { window.open('/checkout', '_self'); }
  return { fetch, addItem, addItems, changeItem, removeItem, getState, subscribe, goToCheckout };
})();

const CartDrawer = (() => {
  let drawer, overlay;
  function init() {
    drawer = document.getElementById('cart-drawer');
    overlay = document.getElementById('cart-overlay');
    if (!drawer) return;
    $$('[data-cart-open]').forEach(el => el.addEventListener('click', open));
    if (overlay) overlay.addEventListener('click', close);
    const closeBtn = drawer.querySelector('[data-cart-close]');
    if (closeBtn) closeBtn.addEventListener('click', close);
    const checkoutBtn = drawer.querySelector('[data-cart-checkout]');
    if (checkoutBtn) checkoutBtn.addEventListener('click', () => Cart.goToCheckout());
    const continueBtn = drawer.querySelector('[data-cart-continue]');
    if (continueBtn) continueBtn.addEventListener('click', close);
    document.addEventListener('keydown', e => { if (e.key === 'Escape' && drawer.classList.contains('is-open')) close(); });
    Cart.subscribe(renderCart);
    // Prefetch the "Complete your look" catalog in the background instead of
    // waiting for the first cart-drawer open to kick off the request — that
    // network round trip is what made the upsell strip feel laggy on first
    // open, especially on mobile connections.
    if ('requestIdleCallback' in window) requestIdleCallback(getUpsellCatalog);
    else setTimeout(getUpsellCatalog, 1000);
  }
  function open() { drawer.classList.add('is-open'); if (overlay) overlay.classList.add('is-open'); lockBodyScroll(); Cart.fetch(); }
  function close() { drawer.classList.remove('is-open'); if (overlay) overlay.classList.remove('is-open'); unlockBodyScroll(); }
  function renderCart(state) {
    $$('.cart-count').forEach(el => { el.textContent = state.item_count; el.classList.toggle('has-items', state.item_count > 0); });
    const body = drawer.querySelector('.cart-body');
    const footer = drawer.querySelector('.cart-footer');
    if (!body) return;
    renderUpsells(state);
    if (state.item_count === 0) {
      body.innerHTML = `<div class="cart-empty"><svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M15.75 10.5V6a3.75 3.75 0 10-7.5 0v4.5m11.356-1.993l1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 01-1.12-1.243l1.264-12A1.125 1.125 0 015.513 7.5h12.974c.576 0 1.059.435 1.119 1.007zM8.625 10.5a.375.375 0 11-.75 0 .375.375 0 01.75 0zm7.5 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" /></svg><p style="font-size:0.9375rem;font-weight:600;">Your bag is empty</p><button class="btn-primary btn-sm" onclick="CartDrawer._close()">Continue shopping</button></div>`;
      if (footer) footer.style.display = 'none';
      return;
    }
    if (footer) footer.style.display = '';
    body.innerHTML = state.items.map(item => `<div class="cart-item" data-key="${item.key}"><div class="cart-item-img">${item.image ? `<img src="${item.image}" alt="${item.title}" width="72" height="72" loading="lazy">` : ''}</div><div class="cart-item-info"><p class="cart-item-title">${item.product_title}</p>${item.variant_title && item.variant_title !== 'Default Title' ? `<p class="cart-item-variant">${item.variant_title}</p>` : ''}<p class="cart-item-price">${formatMoney(item.final_line_price, state.currency)}</p><div class="cart-qty-pill"><button data-qty-dec data-key="${item.key}" data-qty="${item.quantity}" aria-label="Remove one"><svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/></svg></button><span>${item.quantity}</span><button data-qty-inc data-key="${item.key}" data-qty="${item.quantity}" aria-label="Add one"><svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 5v14M5 12h14"/></svg></button></div>${item.properties && item.properties._free_kit ? `<span class="cart-free-badge">FREE with bundle</span>` : `<button class="cart-remove-btn" data-remove data-key="${item.key}" ${item.properties && item.properties._free_kit ? 'data-free-kit="true"' : ''}>Remove</button>`}</div></div>`).join('');
    const subtotalEl = drawer.querySelector('[data-cart-subtotal]');
    if (subtotalEl) subtotalEl.textContent = formatMoney(state.total_price, state.currency);
    $$('[data-qty-dec]', body).forEach(btn => { btn.addEventListener('click', async () => { const key = btn.dataset.key; const qty = parseInt(btn.dataset.qty, 10) - 1; await Cart.changeItem({ key, quantity: Math.max(0, qty) }); }); });
    $$('[data-qty-inc]', body).forEach(btn => { btn.addEventListener('click', async () => { const key = btn.dataset.key; const qty = parseInt(btn.dataset.qty, 10) + 1; await Cart.changeItem({ key, quantity: qty }); }); });
    $$('[data-remove]', body).forEach(btn => {
      btn.addEventListener('click', async () => {
        if (btn.dataset.freeKit === 'true') {
          const cartState = Cart.getState();
          const bundleTier = (cartState.items.find(i => i.properties && i.properties._bundle === 'duo') || {}).properties;
          if (bundleTier) { Toast.show('The free kit is included with your 2-pair package. Remove a pair first.'); return; }
        }
        await Cart.removeItem(btn.dataset.key);
      });
    });
  }
  function formatDollarPrice(priceStr) {
    const amount = parseFloat(priceStr) || 0;
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(amount);
  }
  function showUpsellVariantPopup(product, onConfirm) {
    const existing = document.getElementById('upsell-variant-popup');
    if (existing) existing.remove();
    const overlay = document.createElement('div');
    overlay.id = 'upsell-variant-popup';
    overlay.className = 'upsell-variant-popup-overlay';
    const sheet = document.createElement('div');
    sheet.className = 'upsell-variant-popup-sheet';
    // Shopify's /products.json shape: product.options is an array of
    // {name, values} objects (not strings), and each variant carries its
    // selections as option1/option2/option3 fields (not a variant.options
    // array). Using the wrong shape here threw inside this .map() before
    // the popup ever rendered, so clicking "Add" on any multi-variant
    // product (e.g. SnapMags) silently did nothing.
    // Selection state is also handled via addEventListener + a CSS class
    // rather than inline onclick="..." strings — the previous version built
    // onclick attributes containing escaped quotes inside a double-quoted
    // HTML attribute, which the HTML parser cut short, dumping raw JS as
    // visible button text instead of running it.
    const optionHTML = product.options.map((opt, i) => {
      const optName = typeof opt === 'string' ? opt : opt.name;
      const values = [...new Set(product.variants.map(v => v[`option${i + 1}`]))];
      return `<div class="upsell-variant-group"><p class="upsell-variant-group-label">${optName}</p><div class="upsell-variant-values" data-option="${i}">${values.map(v => `<button type="button" class="upsell-variant-pill" data-opt-idx="${i}" data-val="${v}">${v}</button>`).join('')}</div></div>`;
    }).join('');
    const img = product.images[0];
    sheet.innerHTML = `<div class="upsell-variant-popup-header">${img ? `<img src="${img.src.replace(/(\.[a-z]+)$/, '_100x100$1')}" alt="${product.title}" width="56" height="56" class="upsell-variant-popup-img">` : ''}<div><p class="upsell-variant-popup-title">${product.title}</p><p class="upsell-variant-popup-price">${formatDollarPrice(product.variants[0].price)}</p></div><button type="button" class="upsell-variant-popup-close" aria-label="Close">&times;</button></div>${optionHTML}<button type="button" id="upsell-confirm-btn" class="btn-primary" style="width:100%;margin-top:0.5rem;">Add to bag</button>`;
    overlay.appendChild(sheet);
    document.body.appendChild(overlay);
    overlay.addEventListener('click', e => { if (e.target === overlay) overlay.remove(); });
    sheet.querySelector('.upsell-variant-popup-close').addEventListener('click', () => overlay.remove());
    function getSelectedVals() {
      return product.options.map((_, i) => { const activeBtn = sheet.querySelector(`[data-option="${i}"] .is-selected`); return activeBtn ? activeBtn.dataset.val : null; });
    }
    // A value is pickable if some variant carrying it, combined with
    // whatever is already selected in every OTHER group, is in stock.
    // Recomputed after every click so choosing e.g. Silver correctly
    // greys out Square if Silver+Square only exists as a sold-out variant
    // — this is what stops a customer from ever landing on and adding a
    // combination Shopify itself reports as unavailable.
    function updateAvailability() {
      const selectedVals = getSelectedVals();
      sheet.querySelectorAll('.upsell-variant-pill').forEach(btn => {
        const optIdx = parseInt(btn.dataset.optIdx, 10);
        const val = btn.dataset.val;
        const available = product.variants.some(v => v.available && v[`option${optIdx + 1}`] === val && selectedVals.every((sel, j) => j === optIdx || sel === null || v[`option${j + 1}`] === sel));
        btn.classList.toggle('is-soldout', !available);
        btn.disabled = !available;
        btn.setAttribute('aria-disabled', String(!available));
      });
    }
    sheet.querySelectorAll('.upsell-variant-pill').forEach(btn => {
      btn.addEventListener('click', () => {
        if (btn.disabled) return;
        btn.closest('[data-option]').querySelectorAll('.upsell-variant-pill').forEach(b => b.classList.remove('is-selected'));
        btn.classList.add('is-selected');
        updateAvailability();
      });
    });
    updateAvailability();
    document.getElementById('upsell-confirm-btn').addEventListener('click', async () => {
      const selectedVals = getSelectedVals();
      if (selectedVals.some(v => v === null)) { Toast.show('Please select an option for every choice above'); return; }
      const variant = product.variants.find(v => v.available && selectedVals.every((val, i) => v[`option${i + 1}`] === val));
      if (!variant) { Toast.show('Sorry, that combination is out of stock'); return; }
      overlay.remove();
      await onConfirm(variant.id);
    });
  }
  // The upsell product catalog barely changes within a single browsing
  // session, but renderUpsells() runs on every cart mutation (add, qty
  // change, remove, drawer open). Fetch the candidate list once per page
  // load and reuse it, instead of re-fetching the same /products.json on
  // every single cart change.
  let upsellCatalogPromise = null;
  function getUpsellCatalog() {
    if (!upsellCatalogPromise) {
      upsellCatalogPromise = window.fetch('/collections/all/products.json?limit=8')
        .then(res => res.json())
        .then(json => json.products || [])
        .catch(() => { upsellCatalogPromise = null; return []; });
    }
    return upsellCatalogPromise;
  }
  async function renderUpsells(cartState) {
    const upsellEl = drawer.querySelector('.cart-upsell');
    if (!upsellEl) return;
    try {
      const allProds = await getUpsellCatalog();
      const prods = allProds.filter(p => !cartState.items.some(i => i.product_id === p.id)).slice(0, 4);
      if (!prods.length) { upsellEl.style.display = 'none'; return; }
      const heading = cartState.item_count === 0 ? 'You might like this' : 'Complete your look';
      upsellEl.innerHTML = `<p class="cart-upsell-heading">${heading}</p><div class="cart-upsell-strip">${prods.map(p => { const img = p.images[0]; const price = formatDollarPrice(p.variants[0].price); const hasVars = p.variants.length > 1; const vid = p.variants[0].id; const imgSrc = img ? img.src.replace(/(\.[a-z]+)$/, '_200x200$1') : ''; return `<div class="cart-upsell-item" data-product-id="${p.id}"><div class="cart-upsell-img">${imgSrc ? `<img src="${imgSrc}" alt="${p.title}" width="200" height="200" loading="lazy">` : ''}</div><div class="cart-upsell-details"><p class="cart-upsell-name">${p.title}</p><p class="cart-upsell-price">${price}</p></div><button class="cart-upsell-btn" data-vid="${vid}" data-has-variants="${hasVars}" data-prod-index="${prods.indexOf(p)}">Add</button></div>`; }).join('')}</div>`;
      upsellEl.style.display = '';
      upsellEl.querySelectorAll('.cart-upsell-btn').forEach(btn => {
        btn.addEventListener('click', async () => {
          const hasVariants = btn.dataset.hasVariants === 'true';
          const idx = parseInt(btn.dataset.prodIndex, 10);
          const product = prods[idx];
          async function doAdd(variantId) {
            try { btn.textContent = '...'; await Cart.addItem({ variantId: parseInt(variantId, 10), quantity: 1 }); btn.textContent = 'Added'; setTimeout(() => { btn.textContent = 'Add'; }, 2000); }
            catch { btn.textContent = 'Add'; }
          }
          if (hasVariants && product) showUpsellVariantPopup(product, doAdd);
          else await doAdd(btn.dataset.vid);
        });
      });
    } catch { upsellEl.style.display = 'none'; }
  }
  window.CartDrawer = { _close: close };
  return { init, open, close };
})();

const EmailPopup = (() => {
  const STORAGE_KEY = 'allureis_popup_dismissed';
  const SESSION_KEY = 'allureis_popup_shown';
  const CLAIMED_KEY = 'allureis_popup_claimed';
  // Klaviyo's client endpoint answers 202 to everything it accepts for
  // processing, so this revision pin is the only part of the contract we can
  // actually rely on. Bumping it is a deliberate act — check the changelog.
  const KLAVIYO_REVISION = '2026-07-15';
  let popup, overlay, trigger;
  let capturedEmail = '';
  let knownSubscriber = false;
  // One signup per device. Kept in both localStorage and a year-long cookie so
  // clearing one does not hand the visitor a fresh signup form.
  function hasClaimed() {
    try { if (localStorage.getItem(CLAIMED_KEY)) return true; } catch (_) {}
    return document.cookie.split('; ').indexOf(CLAIMED_KEY + '=1') !== -1;
  }
  function markClaimed() {
    try { localStorage.setItem(CLAIMED_KEY, '1'); localStorage.setItem(STORAGE_KEY, '1'); } catch (_) {}
    document.cookie = CLAIMED_KEY + '=1; path=/; max-age=31536000; SameSite=Lax';
  }
  function init() {
    popup = document.getElementById('email-popup');
    overlay = document.getElementById('popup-overlay');
    trigger = document.getElementById('popup-trigger');
    if (!popup) return;
    // Someone Klaviyo already recognises (arrived from one of our emails, or
    // signed up before) is on the list already, so don't pitch them the list.
    if (window.klaviyo && typeof window.klaviyo.isIdentified === 'function') {
      Promise.resolve(window.klaviyo.isIdentified()).then(v => { knownSubscriber = !!v; }).catch(() => {});
    }
    const closeBtn = popup.querySelector('.popup-close');
    if (closeBtn) closeBtn.addEventListener('click', () => { if (closeBtn.classList.contains('is-locked')) return; dismiss(); });
    popup.querySelectorAll('[data-popup-dismiss]').forEach(el => {
      el.addEventListener('click', () => { if (el.classList.contains('is-locked')) return; dismiss(); });
    });
    if (overlay) overlay.addEventListener('click', () => { if (popup.classList.contains('is-open')) dismiss(); });
    // Only a 'click' listener here — initDraggableNub() below owns real touch/
    // mouse interaction (it tells a drag from a tap via movement distance and
    // opens only on a true tap-and-release). A second unconditional touchend
    // listener used to live here and fire on ANY touchend including right
    // after dragging the nub, which is why sliding it used to open the popup.
    if (trigger) { trigger.addEventListener('click', openFromTrigger); }
    const emailForm = popup.querySelector('[data-popup-form="email"]');
    if (emailForm) emailForm.addEventListener('submit', handleEmailSubmit);
    initSmsStep();
    initCodeCopy();
    // Someone who already claimed the code and comes back through the nub
    // wants the code, not the signup form they already filled in.
    if (hasClaimed()) showStep('done');
    initDraggableNub();
    if (sessionStorage.getItem(SESSION_KEY)) return;
    // The offer is for browsing visitors. It never opens by itself on the
    // product page or the cart page, where it would land on top of the
    // size picker or the checkout button.
    if (/template-(product|cart)/.test(document.body.className)) return;
    if (!localStorage.getItem(STORAGE_KEY)) {
      // Visitors who arrive from a Meta ad (fbclid, or utm_source facebook,
      // instagram, fb, ig or meta) get a much longer quiet period and no
      // exit-intent prompt: they came to look at the product, not to be asked
      // for an email in their first seconds.
      let fromMeta = false;
      try {
        const q = new URLSearchParams(window.location.search);
        fromMeta = q.has('fbclid') || /^(facebook|instagram|fb|ig|meta)/i.test(q.get('utm_source') || '');
        if (fromMeta) sessionStorage.setItem('allureis_from_meta', '1');
        else if (sessionStorage.getItem('allureis_from_meta')) fromMeta = true;
      } catch (_) {}
      if (fromMeta) { setTimeout(open, 60000); return; }
      setTimeout(open, 15000);
      // Don't arm exit-intent until the page has actually been seen for a
      // few seconds — otherwise an accidental mouse move toward the browser
      // chrome in the first moment of a visit triggers the popup before the
      // customer has had a chance to look at anything.
      setTimeout(() => { document.addEventListener('mouseleave', onExitIntent); }, 10000);
    }
  }
  function onExitIntent(e) { if (e.clientY <= 0) { document.removeEventListener('mouseleave', onExitIntent); open(); } }
  // Manually opening via the nub tab is a deliberate action. The popup can be
  // closed at any moment, there is no dismiss lock or countdown.
  function openFromTrigger() { sessionStorage.setItem(SESSION_KEY, '1'); if (popup) popup.classList.add('is-open'); if (overlay) overlay.classList.add('is-open'); lockBodyScroll(); }
  function open() { if (localStorage.getItem(STORAGE_KEY) || hasClaimed() || knownSubscriber) return; sessionStorage.setItem(SESSION_KEY, '1'); if (popup) popup.classList.add('is-open'); if (overlay) overlay.classList.add('is-open'); if (trigger) trigger.classList.add('popup-is-open'); lockBodyScroll(); }
  function close() { if (popup) popup.classList.remove('is-open'); if (overlay) overlay.classList.remove('is-open'); if (trigger) trigger.classList.remove('popup-is-open'); unlockBodyScroll(); }
  function dismiss() { close(); localStorage.setItem(STORAGE_KEY, '1'); }
  function showTrigger() {}
  function initDraggableNub() {
    if (!trigger) return;
    const NUB_SIDE_KEY = 'allureis_nub_side';
    const NUB_Y_KEY = 'allureis_nub_y';
    const DRAG_THRESHOLD = 10;
    let isDragging = false, startX = 0, startY = 0, hasMoved = false, currentSide = 'left', currentTopPct = 82;
    function applyPosition(side, topPct, animate) {
      const clampedY = Math.max(8, Math.min(88, topPct));
      trigger.style.transition = animate ? 'left 0.35s cubic-bezier(0.22,1,0.36,1), right 0.35s cubic-bezier(0.22,1,0.36,1), top 0.35s cubic-bezier(0.22,1,0.36,1), opacity 0.2s' : 'none';
      trigger.style.top = `${clampedY}%`;
      if (side === 'left') { trigger.style.left = '0'; trigger.style.right = 'auto'; trigger.classList.add('nub-left'); }
      else { trigger.style.right = '0'; trigger.style.left = 'auto'; trigger.classList.remove('nub-left'); }
      currentSide = side; currentTopPct = clampedY;
    }
    const savedSide = localStorage.getItem(NUB_SIDE_KEY) || 'left';
    const savedY = parseFloat(localStorage.getItem(NUB_Y_KEY) || '82');
    applyPosition(savedSide, savedY, false);
    function onStart(e) { isDragging = true; hasMoved = false; const pt = e.touches ? e.touches[0] : e; startX = pt.clientX; startY = pt.clientY; trigger.style.transition = 'none'; trigger.style.cursor = 'grabbing'; e.preventDefault(); }
    function onMove(e) {
      if (!isDragging) return;
      const pt = e.touches ? e.touches[0] : e;
      const dx = pt.clientX - startX, dy = pt.clientY - startY;
      if (Math.sqrt(dx * dx + dy * dy) >= DRAG_THRESHOLD) hasMoved = true;
      if (hasMoved) {
        const x = pt.clientX, y = pt.clientY;
        if (x < window.innerWidth / 2) { trigger.style.left = '0'; trigger.style.right = 'auto'; trigger.classList.add('nub-left'); }
        else { trigger.style.right = '0'; trigger.style.left = 'auto'; trigger.classList.remove('nub-left'); }
        const topPct = Math.max(8, Math.min(88, (y / window.innerHeight) * 100));
        trigger.style.top = `${topPct}%`;
      }
    }
    function onEnd(e) {
      if (!isDragging) return;
      isDragging = false; trigger.style.cursor = 'grab';
      if (!hasMoved) { openFromTrigger(); return; }
      const rect = trigger.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2, centerY = rect.top + rect.height / 2;
      const side = centerX < window.innerWidth / 2 ? 'left' : 'right';
      const topPct = Math.max(8, Math.min(88, (centerY / window.innerHeight) * 100));
      applyPosition(side, topPct, true);
      localStorage.setItem(NUB_SIDE_KEY, side);
      localStorage.setItem(NUB_Y_KEY, topPct.toFixed(1));
    }
    trigger.addEventListener('touchstart', onStart, { passive: false });
    window.addEventListener('touchmove', onMove, { passive: true });
    window.addEventListener('touchend', onEnd, { passive: true });
    trigger.addEventListener('mousedown', onStart);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onEnd);
    trigger.removeEventListener('click', openFromTrigger);
  }
  // ── Step machine ──────────
  // Three panels live in the dialog at once; exactly one is visible. The
  // progress dots only count the two panels the customer has to act on —
  // the code reveal is the payoff, not a step.
  function showStep(name) {
    if (!popup) return;
    popup.querySelectorAll('[data-popup-step]').forEach(el => {
      const active = el.dataset.popupStep === name;
      el.hidden = !active;
      el.classList.toggle('is-active', active);
    });
    popup.querySelectorAll('[data-step-dot]').forEach(dot => {
      dot.classList.toggle('is-active', dot.dataset.stepDot === name);
    });
    const dots = popup.querySelector('[data-popup-steps]');
    if (dots) dots.hidden = (name === 'done');
    // Only pull focus on a device with a real pointer. Focusing the phone
    // field on a phone throws up the keyboard the instant the step appears,
    // which covers the consent text the customer is being asked to read.
    const finePointer = window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    if (finePointer && popup.classList.contains('is-open')) {
      const focusTarget = popup.querySelector('[data-popup-step="' + name + '"] input, [data-popup-step="' + name + '"] .btn-primary');
      if (focusTarget) { try { focusTarget.focus({ preventScroll: true }); } catch (_) {} }
    }
  }

  function finish() {
    markClaimed();
    // The success panel names the address the code is going to.
    const emailEl = popup.querySelector('[data-popup-done-email]');
    if (emailEl) emailEl.textContent = capturedEmail || '';
    showStep('done');
  }

  function showError(step, message) {
    const el = popup.querySelector('[data-popup-error="' + step + '"]');
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
  }
  function clearError(step) {
    const el = popup.querySelector('[data-popup-error="' + step + '"]');
    if (el) { el.textContent = ''; el.hidden = true; }
  }

  // ── Klaviyo ──────────
  // One subscribe call, used by both steps. Returns { ok } rather than
  // throwing, because the two callers recover differently: a failed email
  // falls back to Shopify so the lead survives, a failed phone does not
  // (there is nowhere on Shopify to put a marketing phone number).
  //
  // A wrong list ID is the failure mode that actually bit this store — the
  // theme pointed at a list that no longer existed and Klaviyo answered
  // 400 "List not found" on every single signup. That is why a non-2xx is
  // surfaced to the customer instead of being swallowed.
  async function klaviyoSubscribe(listId, profileAttributes) {
    const cfg = window.__klaviyoConfig || {};
    if (!cfg.publicKey || !listId) return { ok: false, reason: 'unconfigured' };
    const payload = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: cfg.source || 'Website Popup',
          profile: { data: { type: 'profile', attributes: profileAttributes } }
        },
        relationships: { list: { data: { type: 'list', id: listId } } }
      }
    };
    try {
      const res = await window.fetch(
        'https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(cfg.publicKey),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'revision': KLAVIYO_REVISION },
          body: JSON.stringify(payload)
        }
      );
      if (res.status === 202 || res.ok) return { ok: true };
      let detail = '';
      try { const body = await res.json(); detail = (body.errors && body.errors[0] && body.errors[0].detail) || ''; } catch (_) {}
      console.warn('[Klaviyo] subscribe failed', res.status, detail);
      return { ok: false, reason: 'http', status: res.status, detail: detail };
    } catch (err) {
      console.warn('[Klaviyo] subscribe error', err);
      return { ok: false, reason: 'network' };
    }
  }

  // There is no second write path on purpose. Shopify's /contact endpoint
  // looks like an obvious fallback and is not one: every fetch() to it comes
  // back 400 "Missing CAPTCHA token", because storefront forms are bound to
  // Shopify's invisible captcha and only a real rendered <form> can satisfy
  // it. Code that posts there is code that quietly does nothing, which is the
  // exact class of bug this popup already shipped once. So Klaviyo is the
  // single path, and a failure is shown to the customer rather than hidden.

  function setBusy(btn, text) {
    if (!btn) return '';
    const original = btn.textContent;
    btn.dataset.originalLabel = original;
    btn.textContent = text;
    btn.disabled = true;
    return original;
  }
  function clearBusy(btn) {
    if (!btn) return;
    if (btn.dataset.originalLabel) btn.textContent = btn.dataset.originalLabel;
    btn.disabled = false;
  }

  // ── Step 1: email ──────────
  async function handleEmailSubmit(e) {
    e.preventDefault();
    const input = popup.querySelector('[data-popup-email]');
    const btn = popup.querySelector('[data-popup-submit]');
    clearError('email');
    const email = input ? input.value.trim() : '';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
      showError('email', 'That email address does not look right.');
      if (input) input.focus();
      return;
    }
    capturedEmail = email;
    setBusy(btn, 'Signing you up…');
    const cfg = window.__klaviyoConfig || {};
    const result = await klaviyoSubscribe(cfg.listId, {
      email: email,
      subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
    });
    if (!result.ok) {
      clearBusy(btn);
      showError('email', 'That did not go through. Please try again.');
      return;
    }
    clearBusy(btn);
    // One signup per device, claimed the moment the email lands, so closing
    // on the SMS step can't send them back to a fresh signup form.
    markClaimed();
    if (cfg.smsListId) showStep('sms');
    else finish('You’re In! Check your inbox.');
  }

  // ── Step 2: SMS (optional, and deliberately separate) ──────────
  // Consent to texts has to be its own affirmative act, so this step has its
  // own form, its own submit button, and a box that starts unchecked. The
  // button stays disabled until both the number parses and the box is ticked.
  function initSmsStep() {
    const form = popup.querySelector('[data-popup-form="sms"]');
    if (!form) return;
    const phone = popup.querySelector('[data-popup-phone]');
    const consent = popup.querySelector('[data-popup-consent]');
    const btn = popup.querySelector('[data-popup-sms-submit]');
    const skip = popup.querySelector('[data-popup-skip]');
    function refresh() {
      if (btn) btn.disabled = !(toE164(phone && phone.value) && consent && consent.checked);
    }
    if (phone) {
      phone.addEventListener('input', () => { formatPhoneField(phone); clearError('sms'); refresh(); });
      phone.addEventListener('blur', refresh);
    }
    if (consent) consent.addEventListener('change', () => { clearError('sms'); refresh(); });
    if (skip) skip.addEventListener('click', () => finish('You’re In! Check your inbox.'));
    form.addEventListener('submit', handleSmsSubmit);
    refresh();
  }

  // Klaviyo needs E.164. It will not tell us when a number is unusable — the
  // client endpoint answers 202 and then silently drops the whole request,
  // profile included — so anything that does not parse here has to be caught
  // here or it vanishes without a trace.
  function toE164(raw) {
    const value = (raw || '').trim();
    if (!value) return null;
    if (value.charAt(0) === '+') {
      const intl = value.slice(1).replace(/\D/g, '');
      return (intl.length >= 8 && intl.length <= 15) ? '+' + intl : null;
    }
    const digits = value.replace(/\D/g, '');
    if (digits.length === 10) return '+1' + digits;
    if (digits.length === 11 && digits.charAt(0) === '1') return '+' + digits;
    return null;
  }

  // Cosmetic North-American grouping while typing. Left alone the moment the
  // customer starts with "+", because that is someone entering a country code
  // and reformatting it would fight them.
  function formatPhoneField(field) {
    if (field.value.trim().charAt(0) === '+') return;
    const digits = field.value.replace(/\D/g, '').slice(0, 10);
    let out = digits;
    if (digits.length > 6) out = '(' + digits.slice(0, 3) + ') ' + digits.slice(3, 6) + '-' + digits.slice(6);
    else if (digits.length > 3) out = '(' + digits.slice(0, 3) + ') ' + digits.slice(3);
    else if (digits.length > 0) out = '(' + digits;
    field.value = out;
  }

  async function handleSmsSubmit(e) {
    e.preventDefault();
    const phoneField = popup.querySelector('[data-popup-phone]');
    const consent = popup.querySelector('[data-popup-consent]');
    const btn = popup.querySelector('[data-popup-sms-submit]');
    clearError('sms');
    const phone = toE164(phoneField && phoneField.value);
    if (!phone) {
      showError('sms', 'Enter a mobile number with its area code.');
      if (phoneField) phoneField.focus();
      return;
    }
    if (!consent || !consent.checked) {
      showError('sms', 'Tick the box to agree to receive texts.');
      return;
    }
    setBusy(btn, 'Signing you up…');
    const cfg = window.__klaviyoConfig || {};
    const attributes = {
      phone_number: phone,
      subscriptions: { sms: { marketing: { consent: 'SUBSCRIBED' } } }
    };
    // Sending the email too merges this onto the profile step 1 just created
    // instead of opening a second, half-blank one.
    if (capturedEmail) attributes.email = capturedEmail;
    const result = await klaviyoSubscribe(cfg.smsListId, attributes);
    clearBusy(btn);
    if (!result.ok) {
      showError('sms', 'That did not go through. Your email is already saved, so you can skip this.');
      return;
    }
    finish('You’re In! Check your inbox and phone.');
  }

  // ── Step 3: code reveal ──────────
  // The code is on screen because email deliverability is not a promise we
  // can keep: a signup whose code only exists in an unopened inbox is a
  // signup that never converts.
  function initCodeCopy() {
    const btn = popup.querySelector('[data-popup-copy]');
    if (!btn) return;
    const hint = popup.querySelector('[data-popup-copy-hint]');
    // "Tap" is wrong on a desktop with a mouse, and the affordance is the
    // whole reason the chip looks clickable.
    const idle = (window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches)
      ? 'Click to copy' : 'Tap to copy';
    if (hint) hint.textContent = idle;
    btn.addEventListener('click', async () => {
      const code = btn.dataset.popupCodeValue || '';
      let copied = false;
      try {
        if (navigator.clipboard && window.isSecureContext) { await navigator.clipboard.writeText(code); copied = true; }
      } catch (_) {}
      if (hint) {
        hint.textContent = copied ? 'Copied' : code;
        setTimeout(() => { hint.textContent = idle; }, 2000);
      }
    });
  }

  return { init, open, close };
})();

const BeforeAfter = (() => {
  function init() { $$('[data-before-after]').forEach(initSlider); }
  function initSlider(container) {
    const after = container.querySelector('.before-after-after');
    const div = container.querySelector('.before-after-divider');
    const handle = container.querySelector('.before-after-handle');
    if (!after || !div || !handle) return;
    let pct = 50, dragging = false, rect = null, rafId = null, pendingX = null;
    after.style.willChange = 'clip-path';
    div.style.willChange = 'left';
    handle.style.willChange = 'left';
    function applyPosition() {
      rafId = null;
      if (pendingX === null || !rect) return;
      pct = Math.min(100, Math.max(0, ((pendingX - rect.left) / rect.width) * 100));
      after.style.clipPath = `inset(0 ${100 - pct}% 0 0)`;
      div.style.left = `${pct}%`;
      handle.style.left = `${pct}%`;
    }
    function setPosition(x) {
      pendingX = x;
      if (rafId === null) rafId = requestAnimationFrame(applyPosition);
    }
    function startDrag() {
      rect = container.getBoundingClientRect();
      dragging = true;
      container.classList.add('is-dragging');
    }
    function endDrag() {
      if (!dragging) return;
      dragging = false;
      container.classList.remove('is-dragging');
    }
    handle.addEventListener('mousedown', e => { startDrag(); e.preventDefault(); });
    container.addEventListener('mousedown', e => { startDrag(); setPosition(e.clientX); });
    window.addEventListener('mouseup', endDrag);
    window.addEventListener('mousemove', e => { if (dragging) setPosition(e.clientX); });
    handle.addEventListener('touchstart', e => { startDrag(); }, { passive: true });
    container.addEventListener('touchstart', e => { startDrag(); setPosition(e.touches[0].clientX); }, { passive: true });
    window.addEventListener('touchend', endDrag);
    window.addEventListener('touchcancel', endDrag);
    window.addEventListener('touchmove', e => { if (!dragging) return; e.preventDefault(); setPosition(e.touches[0].clientX); }, { passive: false });
    rect = container.getBoundingClientRect();
    setPosition(rect.left + container.offsetWidth / 2);
  }
  return { init };
})();

const FAQ = (() => {
  function init() { $$('.faq-item').forEach(item => { const trigger = item.querySelector('.faq-trigger'); if (!trigger) return; trigger.addEventListener('click', () => toggle(item)); }); }
  function toggle(item) {
    const isOpen = item.classList.contains('is-open');
    $$('.faq-item.is-open').forEach(el => { el.classList.remove('is-open'); const t = el.querySelector('.faq-trigger'); if (t) t.setAttribute('aria-expanded', 'false'); });
    if (!isOpen) { item.classList.add('is-open'); const trigger = item.querySelector('.faq-trigger'); if (trigger) trigger.setAttribute('aria-expanded', 'true'); }
  }
  return { init };
})();

// Compact FAQ-style boxes on the product page (Details, Size guide, ...). One
// open at a time, like the FAQ; the panel grows and shrinks with a short height
// transition (see .pdp-acc in design-system.css, none under reduced motion).
const PdpAccordion = (() => {
  function set(item, open) {
    item.classList.toggle('is-open', open);
    const btn = item.querySelector('.pdp-acc__btn');
    const panel = item.querySelector('.pdp-acc__panel');
    if (btn) btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (panel) { if (open) panel.removeAttribute('inert'); else panel.setAttribute('inert', ''); }
  }
  function init() {
    $$('[data-pdp-acc]').forEach(group => {
      group.addEventListener('click', e => {
        const btn = e.target.closest('.pdp-acc__btn');
        if (!btn || !group.contains(btn)) return;
        const item = btn.closest('.pdp-acc__item');
        const willOpen = !item.classList.contains('is-open');
        group.querySelectorAll('.pdp-acc__item.is-open').forEach(other => { if (other !== item) set(other, false); });
        set(item, willOpen);
      });
    });
  }
  return { init };
})();

const ProductPage = (() => {
  // Real variant option values only — shape is Round/Square (never Oval),
  // color is Silver/Gold, size is 6mm/8mm.
  const VARIANT_COLORS = ['Silver', 'Gold'];
  const VARIANT_SHAPES = ['Round', 'Square'];
  const VARIANT_SIZES = ['6mm', '8mm'];
  function randomVariantText() {
    const color = VARIANT_COLORS[Math.floor(Math.random() * VARIANT_COLORS.length)];
    const shape = VARIANT_SHAPES[Math.floor(Math.random() * VARIANT_SHAPES.length)];
    const size = VARIANT_SIZES[Math.floor(Math.random() * VARIANT_SIZES.length)];
    return `${color} ${shape} ${size}`;
  }
  const BUYER_PEOPLE = [
    { name: 'Brianna', city: 'Philadelphia' },
    { name: 'Maya', city: 'Austin' },
    { name: 'Jordan', city: 'Brooklyn' },
    { name: 'Sophie', city: 'Toronto' },
    { name: 'Aaliyah', city: 'Atlanta' },
    { name: 'Andy', city: 'McKinney' },
    { name: 'Quame', city: 'Baltimore' },
    { name: 'Juwaun', city: 'Bronx' },
    { name: 'Sarah', city: 'Denver' },
    { name: 'Diego', city: 'Miami' },
    { name: 'Olivia', city: 'Seattle' },
    { name: 'Marcus', city: 'Chicago' },
  ];
  function pickColorStyle(val) {
    const v = val.toLowerCase();
    if (v.includes('silver') || v.includes('grey') || v.includes('gray')) return '#C0C0C0';
    if (v.includes('gold') || v.includes('rose gold')) return '#C9A24B';
    if (v.includes('black')) return '#0A0A0A';
    if (v.includes('white')) return '#FFFFFF';
    if (v.includes('platinum')) return '#D8D8D8';
    if (v.includes('clear')) return '#F0F0F0';
    return null;
  }
  function swatchNeedsBorder(val) { const v = val.toLowerCase(); return v.includes('white') || v.includes('clear'); }
  function createInstance(sectionEl) {
    const sectionId = sectionEl.dataset.productSection;
    const productData = (window.__productData || {})[sectionId];
    if (!productData) return null;
    // Preselected tier, as on the live site: landing on nothing selected made
    // the ATC read "SELECT YOUR STYLE" with no indication of what to do. 'kit'
    // lifts AOV while still needing only ONE variant pick; duo needs two, which
    // is too much to preselect somebody into. Pages with no kit tier (the
    // replacement kit page) start on the single tier.
    const state = { bundle: sectionEl.querySelector('[data-bundle="kit"]') ? 'kit' : 'single', pair1: {}, pair2: {}, qty: 1, addingToCart: false, upsellAcknowledged: false };
    const buyBox = sectionEl.querySelector('[data-buy-box]');
    const stickyAtc = document.getElementById('sticky-atc');
    const variantPickerEl = sectionEl.querySelector('[data-variant-pickers]');
    const countdownEl = sectionEl.querySelector('[data-countdown]');
    const qtyDecEl = sectionEl.querySelector('[data-qty-dec]');
    const qtyIncEl = sectionEl.querySelector('[data-qty-inc]');
    const qtyValEl = sectionEl.querySelector('[data-qty-val]');
    const variantIdInput = document.getElementById('product-variant-id');
    const quantityInput = document.getElementById('product-quantity');
    const expressWrapper = document.querySelector('[data-express-wrapper]');
    const lowStockEl = sectionEl.querySelector('[data-low-stock]');
    const lowStockCountEl = sectionEl.querySelector('[data-low-stock-count]');
    // Above this, saying "only N left" is not urgency, it is noise.
    const LOW_STOCK_THRESHOLD = 10;
    const bundleProduct = productData.bundleProduct || null;
    const initSource = bundleProduct || productData;
    function findVariant(sel) { return productData.variants.find(v => productData.options.every((name, i) => sel[name] === v.options[i])) || productData.variants[0]; }
    function findBundleVariant(sel) { if (!bundleProduct) return findVariant(sel); return bundleProduct.variants.find(v => bundleProduct.options.every((name, i) => sel[name] === v.options[i])) || bundleProduct.variants[0]; }
    function getPairSource() { return (state.bundle === 'duo' && bundleProduct) ? bundleProduct : productData; }
    function getOptionValues(index) {
      const src = getPairSource();
      const vals = new Set();
      src.variants.forEach(v => { if (v.options[index]) vals.add(v.options[index]); });
      const list = [...vals];
      // Sizes read smallest to largest (6mm, 8mm), whatever order the variants were created in.
      if (/size/i.test(src.options[index] || '') && list.every(v => !isNaN(parseFloat(v)))) list.sort((x, y) => parseFloat(x) - parseFloat(y));
      return list;
    }
    // Keeps the express-checkout form honest. That form posts direct to
    // /checkout with one variant and one quantity, so it can only represent
    // the single tier. The kit and duo tiers are multi-line carts. Gating it
    // here is what stops a duo selection from silently checking out as one
    // pair of whatever variant the page happened to load with.
    function syncVariantInput() {
      const v1 = findVariant(state.pair1);
      if (variantIdInput && v1) variantIdInput.value = v1.id;
      if (quantityInput) quantityInput.value = state.qty;
      if (expressWrapper) {
        expressWrapper.hidden = !(state.bundle === 'single' && isSelectionComplete() && v1 && v1.available);
      }
      // Real low-stock, read from the variant's own inventory. Only shown once
      // a style is actually picked, since "only N left" is meaningless before
      // we know which N. Replaces a hardcoded 32.
      if (lowStockEl) {
        const inv = v1 && typeof v1.inventory === 'number' ? v1.inventory : null;
        const show = isSelectionComplete() && inv !== null && inv > 0 && inv <= LOW_STOCK_THRESHOLD;
        lowStockEl.hidden = !show;
        if (show && lowStockCountEl) lowStockCountEl.textContent = inv;
      }
    }
    function isOptionAvailable(optName, optValue, pairKey) {
      const src = getPairSource();
      const optIdx = src.options.indexOf(optName);
      const pairSel = state[pairKey] || {};
      return src.variants.some(v => {
        if (v.options[optIdx] !== optValue) return false;
        if (!v.available) return false;
        return src.options.every((name, i) => { if (name === optName) return true; const cur = pairSel[name]; return !cur || v.options[i] === cur; });
      });
    }
    // 'ok' (can be bought), 'soldout' (the value exists but every variant with it
    // is out of stock) or 'none' (the store makes no variant with it). Every
    // value is judged on its own, so any option can be picked directly at any
    // time; applyOption then drops only the other picks that cannot go with it.
    function optionState(optName, optValue, pairKey) {
      const src = getPairSource();
      const optIdx = src.options.indexOf(optName);
      const matches = src.variants.filter(v => v.options[optIdx] === optValue);
      if (!matches.length) return 'none';
      return matches.some(v => v.available) ? 'ok' : 'soldout';
    }
    function optionStateLabel(st) { return st === 'none' ? 'not offered' : (st === 'soldout' ? 'sold out' : ''); }
    // Sets one option on one pair. Every other pick on that pair is kept when a
    // buyable variant still exists with it (Silver, Round and 8mm survive a
    // change to Gold, which is made in Round only); a pick that cannot go with
    // the new one is cleared, and returned so the shopper is told. This is what
    // keeps every option directly selectable while an invalid variant can never
    // be assembled.
    function applyOption(pairKey, optName, value) {
      const src = getPairSource();
      const names = (src.options || []).slice().sort((a, b) => optionOrderRank(a) - optionOrderRank(b));
      const sel = state[pairKey];
      sel[optName] = value;
      const keep = { [optName]: value };
      const cleared = [];
      names.filter(n => n !== optName).forEach(n => {
        if (!sel[n]) return;
        const test = Object.assign({}, keep, { [n]: sel[n] });
        const ok = src.variants.some(v => v.available && Object.keys(test).every(k => v.options[src.options.indexOf(k)] === test[k]));
        if (ok) keep[n] = sel[n]; else { cleared.push({ name: n, value: sel[n] }); delete sel[n]; }
      });
      return cleared;
    }
    function clearedNotice(cleared, val) {
      if (cleared.length) Toast.show(cleared.map(c => c.name + ' (' + c.value + ') was cleared because it is not available with ' + val + '.').join(' '));
    }
    function findGalleryVariant() {
      const sel = state.pair1;
      const opts = productData.options || [];
      if (opts.length && opts.every(name => sel[name])) {
        const exact = productData.variants.find(v => opts.every((name, i) => v.options[i] === sel[name]));
        if (exact) return exact;
      }
      // Color + Shape selected but Size not yet chosen: still preview the
      // matching color/shape combination using the first variant that
      // matches on those two options, pulling its real Shopify image.
      const colorName = opts.find(n => n.toLowerCase().includes('color'));
      const shapeName = opts.find(n => n.toLowerCase().includes('shape'));
      if (colorName && shapeName && sel[colorName] && sel[shapeName]) {
        const ci = opts.indexOf(colorName), si = opts.indexOf(shapeName);
        return productData.variants.find(v => v.options[ci] === sel[colorName] && v.options[si] === sel[shapeName]) || null;
      }
      return null;
    }
    function updateGalleryImage() {
      const v1 = findGalleryVariant();
      if (!v1 || !v1.image) return;
      const mainImg = sectionEl.querySelector('#gallery-main-img');
      if (!mainImg || mainImg.src.includes(v1.image)) return;
      mainImg.style.opacity = '0';
      // Drop the static srcset rendered by Liquid for the initial featured
      // image — it only describes that one image, so leaving it in place
      // after a variant swap would let the browser pick a stale srcset
      // candidate over the new src at some viewport widths.
      mainImg.removeAttribute('srcset');
      mainImg.src = v1.image;
      mainImg.onload = () => { mainImg.style.transition = 'opacity 0.25s'; mainImg.style.opacity = '1'; };
      sectionEl.querySelectorAll('.gallery-thumb').forEach(t => { const thumbSrc = (t.dataset.src || '').split('?')[0]; const varSrc = v1.image.split('?')[0]; t.classList.toggle('is-active', thumbSrc.includes(varSrc) || varSrc.includes(thumbSrc)); });
    }
    function updateATCState() {
      const selectionsComplete = isSelectionComplete();
      const v1 = selectionsComplete ? findVariant(state.pair1) : null;
      const available = v1 ? v1.available : false;
      const allATCBtns = [...sectionEl.querySelectorAll('[data-atc]'), ...(stickyAtc ? stickyAtc.querySelectorAll('[data-atc]') : [])];
      allATCBtns.forEach(btn => {
        // Guided page: the main button stays off until every choice is made and
        // names the next one. The sticky bar's button stays tappable while
        // incomplete, but only to jump back to the step that is waiting
        // (handleATC never adds an incomplete selection).
        if (!selectionsComplete) { btn.disabled = false; btn.classList.add('is-selecting'); btn.classList.remove('is-out-of-stock'); btn.textContent = nextStepLabel(); return; }
        btn.classList.remove('is-selecting');
        btn.disabled = !available;
        if (!available) { btn.classList.add('is-out-of-stock'); btn.textContent = 'Out of Stock'; }
        else { btn.classList.remove('is-out-of-stock'); updateATCPrice(); }
      });
    }
    // Names the one thing the shopper still has to pick, in the order the
    // pickers ask for it (Color, Shape, Size). Replaces a generic
    // "Select your style" that did not say what was missing.
    function nextStepLabel() {
      if (!state.bundle) return 'Select an option';
      const src = getPairSource();
      if (!hasRealOptions(src)) return 'Add to bag';
      const names = (src.options || []).slice().sort((a, b) => optionOrderRank(a) - optionOrderRank(b));
      const miss1 = names.find(n => !state.pair1[n]);
      if (miss1) return (needsTwoPicks() ? 'First pair: ' : '') + 'Select ' + miss1.toLowerCase();
      const miss2 = needsTwoPicks() ? names.find(n => !state.pair2[n]) : null;
      if (miss2) return 'Second pair: select ' + miss2.toLowerCase();
      return 'Add to bag';
    }
    // One short line under the price. Before the options are chosen it says
    // the price shown is a starting price and what decides the exact one;
    // after, it names the configuration the price belongs to.
    function updatePriceNote() {
      const noteEl = sectionEl.querySelector('[data-price-note]');
      if (!noteEl) return;
      const src = getPairSource();
      if (!state.bundle || !hasRealOptions(src)) { noteEl.textContent = ''; return; }
      const cur = productData.currency;
      if (isSelectionComplete()) {
        const parts = productData.options.map(n => state.pair1[n]).filter(Boolean);
        let txt = 'Your pick: ' + parts.join(', ') + '.';
        if (needsTwoPicks()) {
          const p2 = productData.options.map(n => state.pair2[n]).filter(Boolean);
          txt = 'Your picks: ' + parts.join(', ') + ' and ' + p2.join(', ') + '.';
        }
        noteEl.textContent = txt;
        return;
      }
      const prices = pair1Matches().map(v => v.price);
      const differs = prices.length && Math.min(...prices) !== Math.max(...prices);
      noteEl.textContent = differs
        ? 'Starting price. The exact price depends on the size you choose.'
        : 'Choose your remaining options to add to bag.';
    }
    function updateATCPrice() {
      if (!isSelectionComplete()) return;
      const v1 = findVariant(state.pair1);
      if (!v1) return;
      let total = v1.price;
      let label = 'Add to bag';
      if (state.bundle === 'kit') { const kp = calcKitPricing(v1); total = kp.price * state.qty; }
      else if (state.bundle === 'duo' && bundleProduct) { const e1 = findBundleVariant(state.pair1) || bundleProduct.variants[0]; const e2 = findBundleVariant(state.pair2) || bundleProduct.variants[0]; total = calcBundlePrice(e1, e2) * state.qty; }
      else if (state.bundle === 'duo') { const v2 = findVariant(state.pair2) || v1; total = calcBundlePrice(v1, v2) * state.qty; }
      else { total = v1.price * state.qty; }
      const priceStr = formatMoney(total, productData.currency);
      const allATCBtns = [...sectionEl.querySelectorAll('[data-atc]'), ...(stickyAtc ? stickyAtc.querySelectorAll('[data-atc]') : [])];
      allATCBtns.forEach(btn => { if (!btn.disabled) { btn.textContent = `${label} · ${priceStr}`; btn.dataset.atcLabel = `${label} · ${priceStr}`; } });
    }
    function updateMainPrice() {
      const priceEl = sectionEl.querySelector('[data-product-price]');
      if (!priceEl) return;
      if (!state.bundle) { priceEl.textContent = `From ${formatMoney(productData.priceMin || productData.variants[0]?.price || 0, productData.currency)}`; return; }
      if (state.bundle === 'kit') {
        if (!pair1Complete()) { priceEl.textContent = 'From ' + formatMoney(calcKitPricing({ price: pair1MinPrice() }).price, productData.currency); return; }
        const kp = calcKitPricing(findVariant(state.pair1));
        priceEl.textContent = formatMoney(kp.price, productData.currency);
        return;
      }
      if (state.bundle === 'duo') {
        const v1 = Object.keys(state.pair1).length > 0 ? findVariant(state.pair1) : null;
        const v2 = Object.keys(state.pair2).length > 0 ? findVariant(state.pair2) : null;
        if (v1 && v2) { const pricing = calcBundlePricing(v1, v2); priceEl.textContent = formatMoney(pricing.price, productData.currency); }
        else { const lowest = minVariantPrice((bundleProduct || productData).variants); priceEl.textContent = `From ${formatMoney(calcBundlePricing({ price: lowest }, { price: lowest }).price, productData.currency)}`; }
        return;
      }
      // A partly chosen pair has no exact variant yet, so it shows the lowest
      // price still possible with what was picked ("From"), never a guess.
      if (pair1Complete()) priceEl.textContent = formatMoney(findVariant(state.pair1).price, productData.currency);
      else priceEl.textContent = `From ${formatMoney(pair1MinPrice(), productData.currency)}`;
    }
    function pair1Complete() {
      const opts = productData.options || [];
      return opts.length > 0 && opts.every(n => state.pair1[n]);
    }
    // Lowest price among variants that still match the options picked so far.
    function pair1Matches() {
      const opts = productData.options || [];
      return (productData.variants || []).filter(v => v.available && opts.every((n, i) => !state.pair1[n] || v.options[i] === state.pair1[n]));
    }
    function pair1MinPrice() {
      const matches = pair1Matches();
      const pool = matches.length ? matches : (productData.variants || []);
      return pool.length ? Math.min(...pool.map(v => v.price)) : (productData.priceMin || 0);
    }
    // Builds a cart line. Everything orderable is a normal in stock buy now,
    // so no line carries a pre-order property any more.
    function pairLine(v, quantity, extra) {
      const props = Object.assign({}, extra || {});
      const line = { variantId: v.id, quantity: quantity };
      if (Object.keys(props).length) line.properties = props;
      return line;
    }
    function calcBundlePricing(v1, v2) {
      const p1 = (v1 && v1.price) || 0;
      const p2 = (v2 && v2.price) || 0;
      // The pairs are charged at full price. The saving on this tier is the
      // stem kit, given away by the "Buy 2 Pairs, Get a Free Stem Kit"
      // automatic discount in Shopify.
      //
      // This used to read `original - 1000`, which advertised $10 off the
      // pairs. No discount in Shopify ever took that $10 off, so the page
      // showed $49.99 while checkout charged $59.98 on every 2-pair order.
      // Verified against orders #667152603 and #667153203, both charged
      // $59.98 with the pairs at full price. Do not reintroduce a discount
      // here without a matching Shopify discount, and note that Shopify
      // applies only ONE product-class automatic discount per order, so a
      // second one cannot stack with the free kit.
      const price = p1 + p2;
      const savings = productData.kitVariantPrice || 2599;
      const original = price + savings;
      return { price, original, savings };
    }
    function calcKitPricing(v1) {
      const pairPrice = (v1 && v1.price) || 0;
      const kitFullPrice = (productData.kitVariantPrice || 2599);
      // Derived from what Shopify actually charges, not hardcoded. The
      // "Buy 1 Pair, Get Stem Kit 50% Off" automatic discount takes
      // floor(kitFullPrice / 2) off the kit, so the kit bills at the
      // remainder. Verified in a live cart at the kit's old $24.99 price: a
      // $29.99 pair plus that kit charged $42.49, the kit discounted by
      // $12.49 down to $12.50.
      //
      // The kit moved to $25.99 on 2026-09-01, so the discount is now $12.99
      // and the kit bills at $13.00. That $13.00 is the whole point of the
      // new price: a pair ending .99 plus $13.00 lands on .99, where the old
      // $12.50 landed on .49. Changing the kit price changes every tier's
      // ending, so re-check the ladder before touching it again.
      //
      // This used to return a hardcoded 4299 / 4799, which showed a price the
      // checkout did not charge. Never hardcode it again.
      const kitDiscount = Math.floor(kitFullPrice / 2);
      const price = pairPrice + (kitFullPrice - kitDiscount);
      const original = pairPrice + kitFullPrice;
      const savings = kitDiscount;
      return { price, original, savings };
    }
    function calcBundlePrice(v1, v2) { return calcBundlePricing(v1, v2).price; }
    // The two-pair package asks the buyer to choose two styles by hand.
    //
    // WARNING: there is no $0 stem kit variant in this store. The kit that is
    // "free" on the two-pair package is the real, paid kit (`free_kit_variant_id`),
    // made free only by the "Buy 2 Pairs, Get a Free Stem Kit" automatic discount.
    // Pause or edit that discount and the buyer pays full price for a kit the
    // page calls free. Read the note in calcBundlePricing before adding any price
    // cut here: Shopify applies only ONE product-class automatic discount per
    // order, so a second cannot stack with the free kit.
    function needsTwoPicks() { return state.bundle === 'duo'; }
    // A product has no real style choice when it carries no options, or its
    // only "option" is Shopify's synthetic single Default Title variant
    // (e.g. the replacement kit). Such products are a complete selection
    // the instant their bundle row is chosen — no swatch step required.
    function hasRealOptions(src) {
      if (!src) return false;
      const opts = src.options || [];
      if (!opts.length) return false;
      if (src.variants && src.variants.length === 1 && src.variants[0].title === 'Default Title') return false;
      return true;
    }
    function isSelectionComplete() {
      if (!state.bundle) return false;
      const src = getPairSource();
      if (!hasRealOptions(src)) return true;
      const requiredOptions = src.options || [];
      if (!requiredOptions.length) return false;
      const pair1Complete = requiredOptions.every(n => state.pair1[n]);
      const pair2Complete = needsTwoPicks() ? requiredOptions.every(n => state.pair2[n]) : true;
      return pair1Complete && pair2Complete;
    }
    function minVariantPrice(list) {
      if (!list || !list.length) return 0;
      const avail = list.filter(v => v.available);
      const pool = avail.length ? avail : list;
      return Math.min(...pool.map(v => v.price));
    }
    function isPairCompleteFor(src, pairSel) {
      if (!hasRealOptions(src)) return true;
      const opts = (src && src.options) || [];
      return opts.length > 0 && opts.every(n => pairSel && pairSel[n]);
    }
    function updateBundleUI() {
      sectionEl.querySelectorAll('[data-bundle]').forEach(btn => { const isSelected = state.bundle !== '' && btn.dataset.bundle === state.bundle; btn.classList.toggle('is-selected', isSelected); btn.setAttribute('aria-pressed', isSelected ? 'true' : 'false'); });
      const variant = findVariant(state.pair1) || productData.variants[0];
      if (!variant) return;
      const singlePrice = variant.price;
      const duoV1 = bundleProduct ? (findBundleVariant(state.pair1) || bundleProduct.variants[0]) : variant;
      const duoV2 = bundleProduct ? (findBundleVariant(state.pair2) || bundleProduct.variants[0]) : (findVariant(state.pair2) || productData.variants[0]);
      const bundlePricing = calcBundlePricing(duoV1, duoV2);
      // Completeness is checked against each row's own product (not the
      // currently-active bundle), and against ALL required options — not
      // just a hardcoded "size" option — so this works for any product,
      // including ones without a literal size option (e.g. the kit).
      const pair1CompleteSelf = isPairCompleteFor(productData, state.pair1);
      const pair2CompleteSelf = isPairCompleteFor(productData, state.pair2);
      const duoSrc = bundleProduct || productData;
      const pair1CompleteDuo = isPairCompleteFor(duoSrc, state.pair1);
      const pair2CompleteDuo = isPairCompleteFor(duoSrc, state.pair2);
      sectionEl.querySelectorAll('[data-bundle-price]').forEach(el => {
        const which = el.dataset.bundlePrice;
        if (which === 'single') {
          if (!pair1CompleteSelf) { el.textContent = 'From ' + formatMoney(minVariantPrice(productData.variants), productData.currency); return; }
          const singleCompare = variant && variant.compareAtPrice && variant.compareAtPrice > singlePrice ? variant.compareAtPrice : null;
          if (singleCompare) el.innerHTML = `<span class="bundle-price-current">${formatMoney(singlePrice, productData.currency)}</span><span class="bundle-price-original">${formatMoney(singleCompare, productData.currency)}</span>`;
          else el.textContent = formatMoney(singlePrice, productData.currency);
        } else if (which === 'kit') {
          if (!pair1CompleteSelf) { const kp = calcKitPricing({ price: minVariantPrice(productData.variants) }); el.textContent = 'From ' + formatMoney(kp.price, productData.currency); return; }
          const kitV = findVariant(state.pair1) || productData.variants[0];
          const kp = calcKitPricing(kitV);
          el.innerHTML = `<span class="bundle-price-current">${formatMoney(kp.price, productData.currency)}</span><span class="bundle-price-original">${formatMoney(kp.original, productData.currency)}</span>`;
        } else {
          if (!pair1CompleteDuo || !pair2CompleteDuo) {
            const minP = minVariantPrice(duoSrc.variants);
            const lowestPricing = calcBundlePricing({ price: minP }, { price: minP });
            el.textContent = 'From ' + formatMoney(lowestPricing.price, productData.currency);
            return;
          }
          el.innerHTML = `<span class="bundle-price-current">${formatMoney(bundlePricing.price, productData.currency)}</span><span class="bundle-price-original">${formatMoney(bundlePricing.original, productData.currency)}</span>`;
        }
      });
      // Packages are sold as sets: only a single pair has a quantity. A kit or
      // two-pair package is always one set, so "2 x two pairs" (and a second
      // free kit) cannot be built by accident.
      const qtyPill = sectionEl.querySelector('.qty-pill');
      if (qtyPill) {
        const single = state.bundle === 'single';
        qtyPill.hidden = !single;
        if (!single && state.qty !== 1) { state.qty = 1; if (qtyValEl) qtyValEl.textContent = '1'; if (quantityInput) quantityInput.value = 1; }
      }
      const kitBlock = sectionEl.querySelector('#kit-variant-block');
      if (kitBlock) kitBlock.style.display = state.bundle === 'single' ? '' : 'none';
    }
    // Display order: Color, then Shape, then Size. Any option name not matching
    // one of these (e.g. a kit's own option) falls back to its natural position,
    // after the three.
    function optionOrderRank(name) {
      const n = name.toLowerCase();
      if (n.includes('color')) return 0;
      if (n.includes('shape')) return 1;
      if (n.includes('size')) return 2;
      return 3;
    }
    function renderPickerInner(pairKey, label) {
      const sel = state[pairKey];
      const src = getPairSource();
      const opts = src.options
        .map((name, i) => ({ name, values: getOptionValues(i) }))
        .sort((a, b) => optionOrderRank(a.name) - optionOrderRank(b.name));
      let priorComplete = true;
      // When sizes are priced differently, show each size's starting price on
      // its button, so the price change after choosing a size is never a surprise.
      const sizeOpt = opts.find(o => /size/i.test(o.name));
      const sizeIdx = sizeOpt ? src.options.indexOf(sizeOpt.name) : -1;
      const sizeFrom = {};
      if (sizeOpt) sizeOpt.values.forEach(val => { const ps = src.variants.filter(v => v.available && v.options[sizeIdx] === val).map(v => v.price); if (ps.length) sizeFrom[val] = Math.min(...ps); });
      const sizePricesDiffer = new Set(Object.values(sizeFrom)).size > 1;
      return `<p class="pair-label">${label}</p>${opts.map(opt => {
        const isColorOpt = opt.name.toLowerCase().includes('color');
        const isComplete = !!sel[opt.name];
        const isLocked = false; // options are independent; unavailable combinations are handled by optionState
        const isActive = !isLocked && !isComplete;
        const stepState = isLocked ? 'locked' : (isComplete ? 'complete' : 'active');
        const swatchesHTML = opt.values.map(val => {
          const colorStyle = pickColorStyle(val);
          const isColor = !!colorStyle && isColorOpt;
          const isSelected = sel[opt.name] === val;
          const needsBorder = isColor && swatchNeedsBorder(val);
          const st = optionState(opt.name, val, pairKey);
          const available = st === 'ok';
          const stLabel = optionStateLabel(st);
          const isDisabled = !available || isLocked;
          if (isColor) return `<button class="color-swatch${isSelected ? ' is-selected' : ''}${!available ? ' is-soldout' : ''}${isLocked ? ' is-locked' : ''}" data-swatch data-pair="${pairKey}" data-opt-name="${opt.name}" data-opt-value="${val}" data-state="${st}" title="${val}${stLabel ? ' (' + stLabel + ')' : ''}" aria-label="${val}${isSelected ? ' (selected)' : ''}${stLabel ? ', ' + stLabel : ''}" ${isDisabled ? 'aria-disabled="true"' : ''} ${isLocked ? 'disabled tabindex="-1"' : ''}><span class="color-swatch-inner${needsBorder ? ' swatch-border' : ''}" style="background:${colorStyle};"></span></button>`;
          return `<button class="pill-swatch${isSelected ? ' is-selected' : ''}${!available ? ' is-soldout' : ''}${isLocked ? ' is-locked' : ''}" data-swatch data-pair="${pairKey}" data-opt-name="${opt.name}" data-opt-value="${val}" data-state="${st}" ${isDisabled ? 'aria-disabled="true"' : ''} ${isLocked ? 'disabled tabindex="-1"' : ''}>${val}${(productData.optionHints && productData.optionHints[val]) ? '<span class="pill-hint">' + productData.optionHints[val] + '</span>' : ''}${(/size/i.test(opt.name) && sizePricesDiffer && sizeFrom[val] != null) ? '<span class="pill-hint pill-hint--price">' + formatMoney(sizeFrom[val], productData.currency) + '</span>' : ''}${stLabel ? '<span class="swatch-soldout-label"> (' + (st === 'none' ? 'not offered' : 'sold out') + ')</span>' : ''}</button>`;
        }).join('');
        priorComplete = priorComplete && isComplete;
        return `<div class="variant-option is-${stepState}" data-opt-key="${opt.name}"><div class="variant-option-header"><span class="variant-option-name">${opt.name}<span class="required-asterisk" aria-hidden="true">*</span>${isComplete ? '<span class="step-check" aria-hidden="true">✓</span>' : ''}</span><span class="variant-option-value">${sel[opt.name] || ''}</span>${(pairKey === 'pair1' && opt.name.toLowerCase().includes('size') && document.getElementById('size-finder-modal')) ? '<button type="button" class="size-finder-link" data-size-finder-open>Size guide</button>' : ''}</div><div class="swatch-row">${swatchesHTML}</div></div>`;
      }).join('')}`;
    }
    // Updates an already-rendered picker in place: swatch selected/locked/
    // soldout state, step locked/active/complete state, and the selected-
    // value label, via classList/attribute mutation only. No innerHTML
    // rewrite, no node removal or reinsertion — this is what runs on every
    // swatch click so the picker never flickers or resets.
    function patchVariantPickers() {
      const pickerEl = sectionEl.querySelector('.bundle-inline-picker');
      if (!pickerEl) return;
      const sides = pickerEl.querySelectorAll('[data-split-side]');
      const scopes = sides.length
        ? [...sides].map(s => ({ el: s, pairKey: s.dataset.splitSide }))
        : [{ el: pickerEl, pairKey: 'pair1' }];
      scopes.forEach(({ el, pairKey }) => {
        const sel = state[pairKey] || {};
        let priorComplete = true;
        el.querySelectorAll('.variant-option').forEach(optEl => {
          const optName = optEl.dataset.optKey;
          const isComplete = !!sel[optName];
          const isLocked = false; // options are independent; unavailable combinations are handled by optionState
          const isActive = !isLocked && !isComplete;
          const stepState = isLocked ? 'locked' : (isComplete ? 'complete' : 'active');
          optEl.className = `variant-option is-${stepState}`;
          const nameEl = optEl.querySelector('.variant-option-name');
          const valueEl = optEl.querySelector('.variant-option-value');
          if (valueEl) valueEl.textContent = sel[optName] || '';
          if (nameEl) {
            let check = nameEl.querySelector('.step-check');
            let badge = nameEl.querySelector('.step-badge');
            if (isComplete) { if (!check) { check = document.createElement('span'); check.className = 'step-check'; check.setAttribute('aria-hidden', 'true'); check.textContent = '✓'; nameEl.appendChild(check); } }
            else if (check) check.remove();
            if (badge) badge.remove();
          }
          optEl.querySelectorAll('[data-swatch]').forEach(swatch => {
            const optValue = swatch.dataset.optValue;
            const isSelected = sel[optName] === optValue;
            const st = optionState(optName, optValue, pairKey);
            const available = st === 'ok';
            const stLabel = optionStateLabel(st);
            const isDisabled = !available || isLocked;
            swatch.dataset.state = st;
            swatch.classList.toggle('is-selected', isSelected);
            swatch.classList.toggle('is-soldout', !available);
            swatch.classList.toggle('is-locked', isLocked);
            if (isDisabled) swatch.setAttribute('aria-disabled', 'true'); else swatch.removeAttribute('aria-disabled');
            if (isLocked) { swatch.setAttribute('disabled', ''); swatch.setAttribute('tabindex', '-1'); } else { swatch.removeAttribute('disabled'); swatch.removeAttribute('tabindex'); }
            if (swatch.hasAttribute('title')) swatch.setAttribute('title', `${optValue}${stLabel ? ' (' + stLabel + ')' : ''}`);
            swatch.setAttribute('aria-label', `${optValue}${isSelected ? ' (selected)' : ''}${stLabel ? ', ' + stLabel : ''}`);
            const lbl = swatch.querySelector('.swatch-soldout-label');
            if (swatch.classList.contains('pill-swatch')) {
              if (stLabel && !lbl) { const s = document.createElement('span'); s.className = 'swatch-soldout-label'; s.textContent = ' (' + (st === 'none' ? 'not offered' : 'sold out') + ')'; swatch.appendChild(s); }
              else if (stLabel && lbl) lbl.textContent = ' (' + (st === 'none' ? 'not offered' : 'sold out') + ')';
              else if (!stLabel && lbl) lbl.remove();
            }
          });
          priorComplete = priorComplete && isComplete;
        });
        // Faded options carry a tooltip on desktop only, so say it in words.
        let hint = el.querySelector('.option-hint');
        const hasNone = [...el.querySelectorAll('[data-swatch]')].some(sw => sw.dataset.state === 'none' && !sw.classList.contains('is-locked'));
        if (hasNone && !hint) {
          hint = document.createElement('p');
          hint.className = 'option-hint';
          hint.textContent = 'Faded options are not made with your current picks. Change another option to see them.';
          el.appendChild(hint);
        } else if (!hasNone && hint) hint.remove();
      });
    }
    function renderVariantPickers() {
      const existingPicker = sectionEl.querySelector('.bundle-inline-picker');
      if (existingPicker) existingPicker.remove();
      if (!state.bundle) return;
      const selectedBtn = sectionEl.querySelector(`[data-bundle="${state.bundle}"]`);
      if (!selectedBtn) return;
      let pickerHTML = '';
      const kitImg = productData.freeKitImageUrl || 'https://cdn.shopify.com/s/files/1/0989/5090/6139/files/repl1.png?v=1768405171';
      // Kit card figures derived from the real kit price and the actual
      // "Buy 1 Pair, Get Stem Kit 50% Off" behaviour, not hardcoded. Shopify
      // takes floor(kitFull / 2) off, so a $25.99 kit bills at $13.00 with a
      // $12.99 discount. The card previously claimed "You save $12.50".
      const _kitFull = productData.kitVariantPrice || 2599;
      const _kitDisc = Math.floor(_kitFull / 2);
      const _kitHalf = _kitFull - _kitDisc;
      const _cur = productData.currency;
      const kitImgHTML = kitImg ? `<div class="free-kit-img"><img src="${kitImg}" alt="4pc Replacement Stem Kit" loading="lazy" width="64" height="64"></div>` : '<div class="free-kit-img free-kit-img--placeholder"></div>';
      if (state.bundle === 'single') {
        if (!hasRealOptions(productData)) { updateStickyAtc(); syncVariantInput(); updateGalleryImage(); updateATCState(); return; }
        pickerHTML = `<div class="variant-picker-block">${renderPickerInner('pair1', 'Choose Your Style')}</div>`;
      }
      else if (state.bundle === 'kit') {
        pickerHTML = `<div class="variant-picker-block">${renderPickerInner('pair1', 'Choose Your Style')}</div><div class="free-kit-card" aria-label="Stem kit at 50% off">${kitImgHTML}<div class="free-kit-body"><p class="free-kit-name">4pc Replacement Stem Kit</p><p class="free-kit-label">Stem Kit included at 50% off</p><div class="free-kit-price-row"><span class="free-kit-original">${formatMoney(_kitFull, _cur)}</span><span class="free-kit-now">${formatMoney(_kitHalf, _cur)}</span><span class="free-kit-badge">You save ${formatMoney(_kitDisc, _cur)}</span></div></div></div>`;
      } else {
        // duo hand-picks two pairs, shown as a split card.
        const splitHTML = `<div class="split-picker-card"><div class="split-picker-side split-picker-side--first" data-split-side="pair1">${renderPickerInner('pair1', 'Choose Your Style, First Pair')}</div><div class="split-picker-divider" aria-hidden="true"></div><div class="split-picker-side split-picker-side--second" data-split-side="pair2">${renderPickerInner('pair2', 'Choose Your Style, Second Pair')}</div></div>`;
        const freeKitHTML = `<div class="free-kit-card" aria-label="Free stem kit included">${kitImgHTML}<div class="free-kit-body"><p class="free-kit-name">4pc Replacement Stem Kit</p><p class="free-kit-label">Included free with your order</p><div class="free-kit-price-row"><span class="free-kit-original">${formatMoney(_kitFull, _cur)}</span><span class="free-kit-now">${formatMoney(0, _cur)}</span><span class="free-kit-badge">You save ${formatMoney(_kitFull, _cur)}</span></div></div></div>`;
        pickerHTML = splitHTML + freeKitHTML;
      }
      const pickerEl = document.createElement('div');
      // Grid-rows height animation (0fr -> 1fr) so the picker grows smoothly
      // into place instead of popping in and instantly shoving the Add to
      // Bag button down — same fix covers the entrance animation and the
      // layout-shift concern, they're one piece of code.
      pickerEl.className = 'bundle-inline-picker';
      pickerEl.innerHTML = `<div class="bundle-inline-picker-inner">${pickerHTML}</div>`;
      // As on the live site, the picker opens directly under the option that was chosen.
      selectedBtn.insertAdjacentElement('afterend', pickerEl);
      // The double rAF gets the open transition (the element must paint at
      // grid-template-rows:0fr once before it can animate to 1fr). But rAF does
      // not fire at all in a hidden or background tab, and since a tier is now
      // preselected on load, that left the picker collapsed at opacity 0 for
      // anyone opening the PDP in a background tab. Timers do fire when hidden,
      // so this is a floor: whichever lands first opens it, and adding the class
      // twice is a no-op.
      requestAnimationFrame(() => requestAnimationFrame(() => pickerEl.classList.add('is-open')));
      setTimeout(() => pickerEl.classList.add('is-open'), 60);
      const activeSlot = pickerEl;
      activeSlot.querySelectorAll('[data-swatch]').forEach(swatch => {
        swatch.addEventListener('click', (e) => {
          if (swatch.classList.contains('is-soldout') || swatch.getAttribute('aria-disabled') === 'true') {
            e.preventDefault(); e.stopPropagation();
            const tip = swatch.querySelector('.swatch-oos-tip') || (() => { const t = document.createElement('span'); t.className = 'swatch-oos-tip'; t.textContent = swatch.dataset.state === 'none' ? 'Not offered with your selection' : 'Out of stock'; t.setAttribute('aria-live', 'polite'); swatch.appendChild(t); return t; })();
            tip.classList.add('is-visible');
            setTimeout(() => tip.classList.remove('is-visible'), 1800);
            return;
          }
          clearedNotice(applyOption(swatch.dataset.pair, swatch.dataset.optName, swatch.dataset.optValue), swatch.dataset.optValue);
          // Patch the existing picker DOM in place (no rebuild/flicker).
          updateBundleUI(); patchVariantPickers(); updateStickyAtc(); syncVariantInput(); updateGalleryImage(); updateATCState(); updateATCPrice(); updateMainPrice(); updatePriceNote();
        });
      });
      updateStickyAtc(); syncVariantInput(); updateGalleryImage(); updateATCState();
    }
    let stickyAtcScrollTicking = false;
    function onScrollStickyAtc() {
      if (!stickyAtc || stickyAtcScrollTicking) return;
      stickyAtcScrollTicking = true;
      requestAnimationFrame(() => {
        stickyAtcScrollTicking = false;
        const mainBtn = sectionEl.querySelector('.atc-btn');
        if (mainBtn) stickyAtc.classList.toggle('is-visible', mainBtn.getBoundingClientRect().bottom < 0);
        else if (buyBox) stickyAtc.classList.toggle('is-visible', buyBox.getBoundingClientRect().bottom < 80);
        // Lets the CSS lift the chat widget clear of the sticky bar.
        document.body.classList.toggle('sticky-atc-on', stickyAtc.classList.contains('is-visible'));
      });
    }
    function updateStickyAtc() {
      if (!stickyAtc) return;
      const detailsEl = stickyAtc.querySelector('[data-sticky-details]');
      const hasSelections = Object.keys(state.pair1).length > 0;
      if (!hasSelections) { if (detailsEl) detailsEl.textContent = ''; return; }
      let detailText = '';
      if (needsTwoPicks()) {
        const v1 = bundleProduct ? (findBundleVariant(state.pair1) || bundleProduct.variants[0]) : (findVariant(state.pair1) || productData.variants[0]);
        const v2 = bundleProduct ? (findBundleVariant(state.pair2) || bundleProduct.variants[0]) : (findVariant(state.pair2) || productData.variants[0]);
        const t1 = v1 && v1.title !== 'Default Title' ? v1.title : '';
        const t2 = v2 && v2.title !== 'Default Title' ? v2.title : '';
        if (t1 || t2) detailText = [t1, t2].filter(Boolean).join('  +  ');
      } else {
        const v1 = findVariant(state.pair1) || productData.variants[0];
        if (v1 && v1.title !== 'Default Title') detailText = productData.options.map((name, i) => state.pair1[name] ? `${state.pair1[name]}` : '').filter(Boolean).join(' · ');
      }
      if (detailsEl) detailsEl.textContent = detailText;
    }
    // Selecting a bundle from the upsell popup must do exactly what clicking
    // that bundle's own card on the page does, so the popup is never a
    // separate decision path with its own logic to drift out of sync.
    function selectBundle(key) {
      state.bundle = key;
      state.upsellAcknowledged = false;
      updateBundleUI(); renderVariantPickers(); updateStickyAtc(); syncVariantInput(); updateATCState(); updateATCPrice(); updateMainPrice(); updatePriceNote();
    }
    // Builds a popup card by cloning the real .bundle-option button straight
    // out of the page's bundle selector (same name, same data-bundle-price
    // node, same markup) rather than hand-writing a duplicate row, so the
    // popup can never show a price that disagrees with the page. Adds a
    // tier thumbnail, the same imagery already used elsewhere on the page
    // for that tier, since the source card carries no image of its own.
    function buildUpsellCard(realBtn) {
      const clone = realBtn.cloneNode(true);
      clone.removeAttribute('aria-pressed');
      clone.classList.remove('is-selected');
      // The scroll-reveal observer never sees this clone, so the class would
      // leave the card at opacity 0 inside the popup.
      clone.classList.remove('reveal');
      const bundleKey = clone.dataset.bundle;
      const imgSrc = bundleKey === 'single'
        ? ((productData.variants[0] && productData.variants[0].image) || '')
        : (productData.freeKitImageUrl || '');
      if (imgSrc) {
        const img = document.createElement('img');
        img.src = imgSrc;
        img.alt = '';
        img.width = 48;
        img.height = 48;
        img.loading = 'lazy';
        img.className = 'upsell-card-img';
        clone.insertBefore(img, clone.firstChild);
      }
      clone.addEventListener('click', () => {
        selectBundle(bundleKey);
        closeBundleUpsell();
        const bundleEl = sectionEl.querySelector('.bundle-selector');
        if (bundleEl) bundleEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
      return clone;
    }
    function openBundleUpsell() {
      const popup = document.getElementById('bundle-upsell-popup');
      const overlay = document.getElementById('popup-overlay');
      if (!popup) { state.upsellAcknowledged = true; handleATC(); return; }
      const optionsEl = popup.querySelector('[data-upsell-options]');
      if (optionsEl) {
        optionsEl.innerHTML = '';
        // Only the two-pair package. The kit tier is left off deliberately:
        // this popup already interrupts an add-to-bag, and five cards in it is
        // the clutter the four-tier layout was designed to avoid. The kit tier
        // is still on the page, one scroll away.
        const otherBundleBtns = [...sectionEl.querySelectorAll('[data-bundle]')].filter(b => b.dataset.bundle === 'duo');
        otherBundleBtns.forEach(btn => optionsEl.appendChild(buildUpsellCard(btn)));
      }
      popup.classList.add('is-open');
      if (overlay) overlay.classList.add('is-open');
      lockBodyScroll();
    }
    function closeBundleUpsell() {
      const popup = document.getElementById('bundle-upsell-popup');
      const overlay = document.getElementById('popup-overlay');
      if (popup) popup.classList.remove('is-open');
      if (overlay) overlay.classList.remove('is-open');
      unlockBodyScroll();
    }
    function wireBundleUpsell() {
      const popup = document.getElementById('bundle-upsell-popup');
      if (!popup) return;
      const closeBtn = popup.querySelector('[data-upsell-close]');
      const declineBtn = popup.querySelector('[data-upsell-decline]');
      if (closeBtn) closeBtn.addEventListener('click', () => { if (closeBtn.classList.contains('is-locked')) return; closeBundleUpsell(); });
      if (declineBtn) declineBtn.addEventListener('click', () => {
        if (declineBtn.classList.contains('is-locked')) return;
        state.upsellAcknowledged = true;
        closeBundleUpsell();
        handleATC();
      });
      const overlay = document.getElementById('popup-overlay');
      if (overlay) overlay.addEventListener('click', () => { if (popup.classList.contains('is-open')) closeBundleUpsell(); });
    }
    async function handleATC() {
      if (state.addingToCart) return;
      if (!state.bundle) { const bundleEl = sectionEl.querySelector('.bundle-selector'); if (bundleEl) bundleEl.scrollIntoView({ behavior: 'smooth', block: 'center' }); Toast.show('Please choose your bundle first'); return; }
      const src = getPairSource();
      const requiredOptions = hasRealOptions(src) ? src.options : [];
      const pair1Missing = requiredOptions.filter(n => !state.pair1[n]);
      const pair2Missing = needsTwoPicks() ? requiredOptions.filter(n => !state.pair2[n]) : [];
      if (pair1Missing.length > 0 || pair2Missing.length > 0) {
        const inlinePicker = sectionEl.querySelector('.bundle-inline-picker');
        const splitCard = sectionEl.querySelector('.split-picker-card');
        const pickerEl = sectionEl.querySelector('[data-variant-pickers]');
        const shakeOptionsByNames = (container, optNames) => { if (!container) return; optNames.forEach(name => { const optEl = container.querySelector(`[data-opt-key="${name}"]`); if (optEl) { optEl.classList.add('option-error'); setTimeout(() => optEl.classList.remove('option-error'), 800); } }); };
        if (splitCard) {
          if (pair1Missing.length > 0) { const side = splitCard.querySelector('[data-split-side="pair1"]'); side && side.classList.add('picker-error'); shakeOptionsByNames(side, pair1Missing); setTimeout(() => side && side.classList.remove('picker-error'), 2000); }
          if (pair2Missing.length > 0) { const side = splitCard.querySelector('[data-split-side="pair2"]'); side && side.classList.add('picker-error'); shakeOptionsByNames(side, pair2Missing); setTimeout(() => side && side.classList.remove('picker-error'), 2000); }
          splitCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else if (inlinePicker) { shakeOptionsByNames(inlinePicker, pair1Missing); inlinePicker.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
        else if (pickerEl) { pickerEl.scrollIntoView({ behavior: 'smooth', block: 'center' }); pickerEl.style.transition = 'box-shadow 0.3s'; pickerEl.style.boxShadow = '0 0 0 2px var(--fg)'; setTimeout(() => { pickerEl.style.boxShadow = ''; }, 1200); }
        const missing = [...new Set([...pair1Missing, ...pair2Missing])];
        Toast.show(`Please select your ${missing.join(', ').toLowerCase()}`);
        return;
      }
      const v1check = findVariant(state.pair1);
      if (!v1check || !v1check.available) { Toast.show('This option is out of stock'); return; }
      if (needsTwoPicks()) { const v2check = findVariant(state.pair2); if (!v2check || !v2check.available) { Toast.show('Your second pair selection is out of stock'); return; } }
      // The "Wait, save more?" popup used to open on every single-pair add
      // to bag. Single pair is now the default tier, so that would put a
      // popup in front of every first purchase. It only opens when the
      // product section's "Show bundle popup" setting is on (off by default);
      // the bundles stay visible on the page and in the cart drawer.
      if (state.bundle === 'single' && !state.upsellAcknowledged && productData.upsellPopup) { openBundleUpsell(); return; }
      state.addingToCart = true;
      setATCLoading(true);
      try {
        if (state.bundle === 'kit') {
          const v1 = findVariant(state.pair1);
          if (!v1 || !v1.available) { Toast.show('This option is out of stock'); return; }
          // Add the kit variant so the "Buy 1 Pair, Get Stem Kit 50% Off"
          // automatic discount applies and charges the correct $13.00.
          // This is the same variant the two-pair package adds as its "free"
          // kit; only the discount that fires differs. See the WARNING above
          // needsTwoPicks.
          const kitId = productData.kitVariantId;
          const items = [pairLine(v1, state.qty)];
          if (kitId) items.push({ variantId: kitId, quantity: 1, properties: { _half_off_kit: 'true', _note: 'Stem kit half off' } });
          await Cart.addItems(items);
        } else if (state.bundle === 'duo' && bundleProduct) {
          const e1 = findBundleVariant(state.pair1);
          const e2 = findBundleVariant(state.pair2);
          const kit = productData.variants[0];
          if (!e1 || !e1.available) { Toast.show('First pair is out of stock'); return; }
          if (!e2 || !e2.available) { Toast.show('Second pair is out of stock'); return; }
          await Cart.addItems([pairLine(e1, state.qty), pairLine(e2 || e1, state.qty), { variantId: kit.id, quantity: 1, properties: { _free_kit: 'true' } }]);
        } else if (state.bundle === 'duo') {
          const v1 = findVariant(state.pair1);
          const v2 = findVariant(state.pair2);
          if (!v1 || !v1.available) { Toast.show('First pair is out of stock'); return; }
          if (!v2 || !v2.available) { Toast.show('Second pair is out of stock'); return; }
          const freeKitId = productData.freeKitVariantId;
          const items = [pairLine(v1, state.qty), pairLine(v2 || v1, state.qty)];
          if (freeKitId) items.push({ variantId: freeKitId, quantity: 1, properties: { _free_kit: 'true', _bundle: 'duo' } });
          await Cart.addItems(items);
        } else {
          const v1 = findVariant(state.pair1);
          if (!v1 || !v1.available) { Toast.show('This option is out of stock'); return; }
          await Cart.addItem(pairLine(v1, state.qty));
        }
        Toast.show('Added to bag');
        CartDrawer.open();
      } catch (err) {
        const msg = err.message || '';
        if (msg.toLowerCase().includes('all variants are sold out') || msg.toLowerCase().includes('cannot be added')) Toast.show('Sorry, this item is out of stock');
        else Toast.show(msg || 'Could not add to bag. Please try again.');
      } finally { state.addingToCart = false; setATCLoading(false); }
    }
    function setATCLoading(loading) {
      const atcBtns = [...sectionEl.querySelectorAll('[data-atc]'), ...(stickyAtc ? stickyAtc.querySelectorAll('[data-atc]') : [])];
      atcBtns.forEach(btn => { btn.disabled = loading; if (loading) { if (!btn.dataset.atcLabel) btn.dataset.atcLabel = btn.textContent.trim(); btn.innerHTML = '<span class="spinner"></span>'; } else { btn.textContent = btn.dataset.atcLabel || 'Add to cart'; } });
    }
    function initGallery() {
      const mainImg = sectionEl.querySelector('#gallery-main-img');
      const thumbs = [...sectionEl.querySelectorAll('.gallery-thumb')];
      const dots = [...sectionEl.querySelectorAll('.gallery-dot')];
      const prevBtn = sectionEl.querySelector('#gallery-prev');
      const nextBtn = sectionEl.querySelector('#gallery-next');
      let currentIdx = 0;
      // Photos are shown whole at their own proportions (the store mixes 3:2
      // and 2:3 images), so the frame follows whichever image has loaded.
      const frame = sectionEl.querySelector('#gallery-main');
      if (mainImg && frame) {
        const fit = () => { if (mainImg.naturalWidth && mainImg.naturalHeight) frame.style.aspectRatio = mainImg.naturalWidth + ' / ' + mainImg.naturalHeight; };
        mainImg.addEventListener('load', fit);
        if (mainImg.complete) fit();
      }
      function goTo(idx) {
        const count = thumbs.length;
        if (!count) return;
        currentIdx = ((idx % count) + count) % count;
        const thumb = thumbs[currentIdx];
        if (mainImg && thumb) {
          mainImg.style.opacity = '0.6';
          // Same fix as updateGalleryImage(): the static srcset rendered by
          // Liquid only describes the original featured image, so it must
          // be dropped or the browser keeps re-picking a stale srcset
          // candidate over the new src and the image never visually changes.
          mainImg.removeAttribute('srcset');
          mainImg.removeAttribute('sizes');
          mainImg.src = thumb.dataset.src || mainImg.src;
          mainImg.alt = thumb.dataset.alt || '';
          mainImg.onload = () => { mainImg.style.opacity = '1'; };
        }
        thumbs.forEach((t, i) => t.classList.toggle('is-active', i === currentIdx));
        dots.forEach((d, i) => d.classList.toggle('is-active', i === currentIdx));
      }
      thumbs.forEach((thumb, i) => { thumb.addEventListener('click', () => goTo(i)); });
      if (prevBtn) prevBtn.addEventListener('click', () => goTo(currentIdx - 1));
      if (nextBtn) nextBtn.addEventListener('click', () => goTo(currentIdx + 1));
      // Only the small 160px thumbnail is fetched up front — the full-res
      // image goTo() swaps in on swipe/tap was never downloaded, so the
      // very first swipe to each image waits on a network fetch and feels
      // laggy no matter how fast the handler itself runs. Prefetch every
      // full-res gallery image in the background right after init so by
      // the time a customer swipes, the image is already in cache.
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => { thumbs.forEach(t => { if (t.dataset.src) { const img = new Image(); img.src = t.dataset.src; } }); });
      } else {
        setTimeout(() => { thumbs.forEach(t => { if (t.dataset.src) { const img = new Image(); img.src = t.dataset.src; } }); }, 1000);
      }
      const galleryEl = sectionEl.querySelector('#gallery-main');
      if (galleryEl && thumbs.length > 1) {
        let touchStartX = 0, touchStartY = 0, isSwiping = false;
        galleryEl.addEventListener('touchstart', e => { touchStartX = e.touches[0].clientX; touchStartY = e.touches[0].clientY; isSwiping = false; }, { passive: true });
        galleryEl.addEventListener('touchmove', e => { const dx = e.touches[0].clientX - touchStartX; const dy = e.touches[0].clientY - touchStartY; if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 10) isSwiping = true; }, { passive: true });
        galleryEl.addEventListener('touchend', e => { if (!isSwiping) return; const dx = e.changedTouches[0].clientX - touchStartX; if (Math.abs(dx) > 40) goTo(dx < 0 ? currentIdx + 1 : currentIdx - 1); isSwiping = false; }, { passive: true });
      }
    }
    function initTabs() {
      $$('.tab-btn').forEach(btn => {
        btn.addEventListener('click', () => {
          const target = btn.dataset.tab;
          const tablist = btn.closest('[role="tablist"]');
          const container = tablist ? tablist.parentElement : document;
          $$(`.tab-btn`, container).forEach(b => { b.classList.remove('is-active'); b.setAttribute('aria-selected', 'false'); });
          $$(`.tab-panel`, container).forEach(p => p.classList.remove('is-active'));
          btn.classList.add('is-active');
          btn.setAttribute('aria-selected', 'true');
          const panel = container.querySelector('#tab-' + target);
          if (panel) panel.classList.add('is-active');
        });
      });
    }
    function startCountdown() {
      if (!countdownEl) return;
      const midnight = new Date();
      midnight.setHours(23, 59, 59, 0);
      const target = midnight.getTime();
      const tick = () => { const diff = Math.max(0, target - Date.now()); const h = Math.floor(diff / 3_600_000); const m = Math.floor((diff % 3_600_000) / 60_000); const s = Math.floor((diff % 60_000) / 1000); const pad = n => String(n).padStart(2, '0'); countdownEl.textContent = `${pad(h)}:${pad(m)}:${pad(s)}`; };
      tick();
      setInterval(tick, 1000);
    }
    function startBuyerRotation() {
      const el = sectionEl.querySelector('[data-buyer-notification]');
      if (!el) return;
      let order = BUYER_PEOPLE.map((_, i) => i);
      let cursor = 0;
      let lastIdx = -1;
      function shuffle() {
        for (let i = order.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1));[order[i], order[j]] = [order[j], order[i]]; }
        if (order[0] === lastIdx && order.length > 1) [order[0], order[1]] = [order[1], order[0]];
      }
      shuffle();
      const nameEl = el.querySelector('[data-buyer-name]');
      const cityEl = el.querySelector('[data-buyer-city]');
      const variantEl = el.querySelector('[data-buyer-variant]');
      const agoEl = el.querySelector('[data-buyer-ago]');
      const update = () => {
        if (cursor >= order.length) { shuffle(); cursor = 0; }
        const b = BUYER_PEOPLE[order[cursor]];
        lastIdx = order[cursor];
        cursor++;
        // Always a fresh "just happened" timestamp — never older than 60 minutes.
        const minsAgo = 1 + Math.floor(Math.random() * 45);
        if (nameEl) nameEl.textContent = b.name;
        if (cityEl) cityEl.textContent = b.city;
        if (variantEl) variantEl.textContent = randomVariantText();
        if (agoEl) agoEl.textContent = minsAgo === 1 ? '1 min ago' : `${minsAgo} min ago`;
      };
      update();
      setInterval(update, 8000);
    }
    function startViewerFluctuation() {
      const viewerPill = sectionEl.querySelector('[data-viewer-pill]');
      const countEl = viewerPill && viewerPill.querySelector('[data-viewer-count]');
      if (!countEl) return;
      const base = parseInt(countEl.textContent, 10) || 17;
      const min = Math.max(5, base - 8);
      const max = base + 12;
      let current = base;
      function flicker() {
        const delta = Math.floor(Math.random() * 3) + 1;
        const dir = Math.random() > 0.45 ? 1 : -1;
        current = Math.min(max, Math.max(min, current + delta * dir));
        // Only the number's own text node is touched — icon and surrounding
        // markup are never removed/reinserted, so the pill can never
        // momentarily collapse or change width unexpectedly.
        countEl.style.transition = 'opacity 0.3s';
        countEl.style.opacity = '0';
        setTimeout(() => { countEl.textContent = String(current); countEl.style.opacity = '1'; }, 300);
        const nextMs = (20 + Math.floor(Math.random() * 20)) * 1000;
        setTimeout(flicker, nextMs);
      }
      setTimeout(flicker, (20 + Math.floor(Math.random() * 20)) * 1000);
    }
    function startLowStockFluctuation() {
      const countEl = sectionEl.querySelector('[data-low-stock-count]');
      if (!countEl) return;
      const base = parseInt(countEl.textContent, 10) || 5;
      const min = Math.max(2, base - 2);
      const max = base + 3;
      let current = base;
      function flicker() {
        const dir = Math.random() > 0.6 ? 1 : -1;
        current = Math.min(max, Math.max(min, current + dir));
        countEl.style.transition = 'opacity 0.3s';
        countEl.style.opacity = '0';
        setTimeout(() => { countEl.textContent = String(current); countEl.style.opacity = '1'; }, 300);
        const nextMs = (45 + Math.floor(Math.random() * 45)) * 1000;
        setTimeout(flicker, nextMs);
      }
      setTimeout(flicker, (45 + Math.floor(Math.random() * 45)) * 1000);
    }
    function wire() {
      sectionEl.querySelectorAll('[data-bundle]').forEach(btn => {
        btn.addEventListener('click', (e) => {
          // Tapping the bundle that is already selected does nothing. It used
          // to clear the selection (and every option with it), which left a
          // shopper with no bundle and no visible next step.
          if (state.bundle !== btn.dataset.bundle) selectBundle(btn.dataset.bundle);
        });
      });
      wireBundleUpsell();
      if (qtyDecEl) qtyDecEl.addEventListener('click', () => { state.qty = Math.max(1, state.qty - 1); if (qtyValEl) qtyValEl.textContent = state.qty; updateStickyAtc(); syncVariantInput(); updateATCPrice(); });
      if (qtyIncEl) qtyIncEl.addEventListener('click', () => { state.qty++; if (qtyValEl) qtyValEl.textContent = state.qty; updateStickyAtc(); syncVariantInput(); updateATCPrice(); });
      sectionEl.querySelectorAll('[data-atc]').forEach(btn => btn.addEventListener('click', handleATC));
      if (stickyAtc) stickyAtc.querySelectorAll('[data-atc]').forEach(btn => btn.addEventListener('click', handleATC));
      if (buyBox && stickyAtc) window.addEventListener('scroll', onScrollStickyAtc, { passive: true });
      sectionEl.querySelectorAll('[data-kit-swatch]').forEach(swatch => {
        swatch.addEventListener('click', () => {
          const idx = swatch.dataset.optIndex;
          sectionEl.querySelectorAll(`[data-kit-swatch][data-opt-index="${idx}"]`).forEach(s => { s.classList.remove('is-selected'); });
          swatch.classList.add('is-selected');
          const valEl = sectionEl.querySelector(`#kit-opt-val-${idx}`);
          if (valEl) valEl.textContent = swatch.dataset.optValue;
          if (variantIdInput) variantIdInput.value = swatch.dataset.variantId;
          updateATCState();
        });
      });
      // startLowStockFluctuation() is deliberately NOT called. It random-walked
      // the "Only N left" number up and down every 45-90s, which made a real,
      // inventory-derived count into an invented one. The count now stays at
      // whatever syncVariantInput() read off the selected variant. The other
      // three calls here early-return: their markup was removed in the
      // 2026-07-30 fake-urgency cleanup and was never re-added.
      initGallery(); initTabs(); startCountdown(); startBuyerRotation(); startViewerFluctuation();
      renderVariantPickers(); updateBundleUI(); updateStickyAtc(); updateATCState(); updateMainPrice(); updatePriceNote();
    }
    wire();
    // Sets one option (e.g. Size = 8mm) the same way tapping its swatch does.
    // Used by the size finder and by ?size=8mm links. Returns false, and
    // changes nothing, when the value does not exist or is not purchasable
    // with the shopper's other picks. It never picks a style for the shopper
    // beyond the one option it is given.
    function setOptionValue(name, value) {
      const src = getPairSource();
      const idx = (src.options || []).findIndex(n => n.toLowerCase() === String(name).toLowerCase());
      if (idx < 0) return false;
      const optName = src.options[idx];
      const val = getOptionValues(idx).find(v => v.toLowerCase() === String(value).toLowerCase());
      if (!val) return false;
      let applied = false;
      const cleared = [];
      ['pair1', 'pair2'].forEach(k => {
        if (optionState(optName, val, k) !== 'ok') return;
        applyOption(k, optName, val).forEach(c => cleared.push(c));
        if (k === 'pair1' || needsTwoPicks()) applied = true;
      });
      if (!applied) return false;
      clearedNotice(cleared, val);
      updateBundleUI(); patchVariantPickers(); updateStickyAtc(); syncVariantInput(); updateGalleryImage(); updateATCState(); updateATCPrice(); updateMainPrice(); updatePriceNote();
      return true;
    }
    const api = { state, findVariant, setOption: setOptionValue };
    if (!window.SnapMagsPDP) window.SnapMagsPDP = api;
    try {
      const qs = new URLSearchParams(window.location.search);
      // Deep links from ads and emails. ?package=kit|duo|single picks the
      // package. ?variant=ID fills that variant's options. ?size=, ?color= and
      // ?shape= fill one option each. Anything invalid is ignored, nothing is
      // chosen that is not offered, and every other parameter is left alone.
      const pkg = qs.get('package');
      if (pkg && ['single', 'kit', 'duo'].indexOf(pkg) !== -1 && state.bundle !== pkg && sectionEl.querySelector('[data-bundle="' + pkg + '"]')) selectBundle(pkg);
      const vid = qs.get('variant');
      if (vid) {
        const dv = productData.variants.find(v => String(v.id) === String(vid));
        if (dv && dv.available) (productData.options || []).forEach((n, i) => setOptionValue(n, dv.options[i]));
      }
      ['color', 'shape'].forEach(k => { const val = qs.get(k); if (val) setOptionValue(k, val); });
      const qSize = qs.get('size');
      if (qSize && setOptionValue('Size', qSize) && qs.get('from') === 'finder') {
        Toast.show('Size ' + qSize + ' selected. You can change it below.', 4200);
      }
    } catch (e) { /* URLSearchParams unavailable: nothing to preselect */ }
    return api;
  }
  function initFBT() {
    $$('[data-fbt-add]').forEach(btn => {
      btn.addEventListener('click', async e => {
        e.preventDefault();
        const variantId = btn.dataset.variantId;
        if (!variantId) return;
        try { btn.textContent = '...'; await Cart.addItem({ variantId, quantity: 1 }); btn.textContent = 'Added'; setTimeout(() => { btn.textContent = 'Add'; }, 2000); CartDrawer.open(); }
        catch (err) { btn.textContent = 'Error'; Toast.show(err.message || 'Could not add'); }
      });
    });
  }
  function init() { $$('[data-product-section]').forEach(el => createInstance(el)); }
  return { init, initFBT };
})();

const ScrollReveal = (() => {
  function init() {
    const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion || !('IntersectionObserver' in window)) { return; } // content simply stays visible: nothing is hidden for the reveal
    const observer = new IntersectionObserver((entries) => { entries.forEach(entry => { if (entry.isIntersecting) { entry.target.classList.add('is-revealed'); observer.unobserve(entry.target); } }); }, { rootMargin: '0px 0px -60px 0px', threshold: 0.08 });
    const selectors = ['.how-card', '.benefit-card', '.review-masonry-card', '.review-card', '.product-card', '.tiktok-card', '.faq-item', '.section-label', '.reviews-masonry-heading', '.tiktok-heading', '.featured-content h2', '.how-grid', '.benefits-grid', '.instagram-item', '.instagram-strip', '.bundle-option', '.before-after-section', '.press-bar', '.cta-section', '.fbt-card'];
    // Sections added in the 2026-10 redesign. Headings and cards fade up once as
    // they enter view; the buy box on the product page is left alone on purpose.
    // Siblings in a row or list are staggered a little (capped) so a row of
    // cards arrives as one gesture rather than all at once.
    const staggered = ['.diff__points li', '.steps__item', '.worn-videos__item', '.featured__card', '.diff__photos', '.size-finder-section__card', '.ds-head', '.reviews-compact__summary', '.pdp-points'];
    staggered.forEach(sel => $$(sel).forEach(el => {
      if (el.closest('#buy-box') && !el.classList.contains('pdp-points')) return;
      const sibs = el.parentElement ? [...el.parentElement.children].filter(c => c.matches(sel)) : [el];
      const i = Math.max(0, sibs.indexOf(el));
      if (i > 0) el.style.transitionDelay = Math.min(i, 4) * 70 + 'ms';
      el.classList.add('reveal'); observer.observe(el);
    }));
    selectors.forEach(sel => { $$(sel).forEach(el => { el.classList.add('reveal'); observer.observe(el); }); });
    $$('.animate-fade-up').forEach(el => { if (!el.classList.contains('reveal')) { el.classList.add('reveal'); observer.observe(el); } });
  }
  return { init };
})();

// Shopify injects its native cookie-consent banner asynchronously via
// content_for_header, sometimes after this script runs — the CSS `display:
// none` rule alone can lose that race. Watch for it and hide it as soon as
// it appears, without touching the underlying consent-tracking logic.
(function hideCookieBanner() {
  const banner = document.getElementById('shopify-pc__banner');
  if (banner) { banner.style.display = 'none'; return; }
  const observer = new MutationObserver(() => {
    const b = document.getElementById('shopify-pc__banner');
    if (b) { b.style.display = 'none'; observer.disconnect(); }
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
})();

document.addEventListener('DOMContentLoaded', () => {
  Header.init();
  CartDrawer.init();
  EmailPopup.init();
  BeforeAfter.init();
  FAQ.init();
  PdpAccordion.init();
  ScrollReveal.init();
  ProductPage.init();
  ProductPage.initFBT();
  Cart.fetch();
  const shopBtn = document.getElementById('homepage-shop-btn');
  if (shopBtn) {
    const featuredSection = document.getElementById('featured-product');
    if (featuredSection) {
      let visible = false, ticking = false;
      function updateShopBtn() { ticking = false; const rect = featuredSection.getBoundingClientRect(); const shouldShow = rect.bottom < 0; if (shouldShow !== visible) { visible = shouldShow; shopBtn.classList.toggle('is-visible', shouldShow); } }
      window.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(updateShopBtn); } }, { passive: true });
      updateShopBtn();
    }
  }
});

// ── Signup success + inline signup forms ──────────
// One success state for every signup surface: a green check that draws
// itself (CSS in theme.css, .al-check), "You're In!", and the address the
// personal code is going to.
function alEscape(s) {
  return String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}
function alSuccessHTML(email, note, withShopButton) {
  return '<div class="al-success" role="status">'
    + '<svg class="al-check" viewBox="0 0 52 52" aria-hidden="true" focusable="false"><circle class="al-check__circle" cx="26" cy="26" r="24"/><path class="al-check__mark" d="M15 27l7 7 15-15"/></svg>'
    + '<p class="al-success__title">You’re In!</p>'
    + (email ? '<p class="al-success__email">' + alEscape(email) + '</p>' : '')
    + '<p class="al-success__note">' + note + '</p>'
    + (withShopButton ? '<a class="btn-primary" href="/products/snapmags" style="width:100%;display:flex;align-items:center;justify-content:center;text-decoration:none;margin-top:1rem;">Shop SnapMags</a>' : '')
    + '<p class="al-success__hint">Not there in 5 minutes? Check spam or promotions.</p>'
    + '</div>';
}

// Homepage and footer signup forms (form[data-inline-signup]). They go through
// the same Klaviyo list as the popup, so they get the welcome flow and a
// personal code. Underneath each is a real Shopify customer form: if Klaviyo
// is unconfigured or refuses, the browser submits that instead, so the lead
// is never lost.
(function InlineSignup() {
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  function markClaimed() {
    try { localStorage.setItem('allureis_popup_claimed', '1'); localStorage.setItem('allureis_popup_dismissed', '1'); } catch (_) {}
    document.cookie = 'allureis_popup_claimed=1; path=/; max-age=31536000; SameSite=Lax';
  }
  async function subscribe(cfg, email, source) {
    const payload = { data: { type: 'subscription', attributes: { custom_source: source, profile: { data: { type: 'profile', attributes: { email, subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } } } } } }, relationships: { list: { data: { type: 'list', id: cfg.listId } } } } };
    try {
      const res = await window.fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(cfg.publicKey), { method: 'POST', headers: { 'Content-Type': 'application/json', 'revision': '2026-07-15' }, body: JSON.stringify(payload) });
      return res.ok || res.status === 202;
    } catch (_) { return false; }
  }
  function bind(form) {
    form.addEventListener('submit', async (e) => {
      const cfg = window.__klaviyoConfig || {};
      if (!cfg.listId || !cfg.publicKey) return; // native Shopify submit
      e.preventDefault();
      const input = form.querySelector('input[type="email"]');
      const email = input ? input.value.trim() : '';
      if (!EMAIL_RE.test(email)) {
        if (input) { input.setCustomValidity('Enter a valid email address.'); input.reportValidity(); input.setCustomValidity(''); input.focus(); }
        return;
      }
      const btn = form.querySelector('[type="submit"]');
      if (btn) { btn.disabled = true; btn.textContent = 'Signing you up…'; }
      const ok = await subscribe(cfg, email, form.dataset.inlineSignup || 'Website Signup');
      if (!ok) { form.submit(); return; } // fall back to Shopify's own form
      markClaimed();
      const holder = document.createElement('div');
      holder.innerHTML = alSuccessHTML(email, 'Your personal 10% code is on its way to your inbox.', false);
      form.replaceWith(holder.firstChild);
    });
  }
  function init() { document.querySelectorAll('form[data-inline-signup]').forEach(bind); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();


/* ==========
   DIALOG FOCUS HANDLING
   The email popup, the bundle popup, the cart drawer and the mobile menu all
   behave the same way for keyboard and screen-reader users: focus moves into
   the dialog when it opens, Tab stays inside it, Escape closes it, and focus
   goes back to whatever opened it. Each dialog's own open/close code is left
   alone; this watches the is-open class they already toggle.
   ========== */
(function dialogFocus() {
  const DIALOGS = [
    { sel: '#email-popup', close: '.popup-close' },
    { sel: '#bundle-upsell-popup', close: '[data-upsell-close]' },
    { sel: '#cart-drawer', close: '[data-cart-close]' },
    { sel: '#mobile-nav', close: '#mobile-nav-close', opener: '#hamburger-btn' },
    { sel: '#size-finder-modal', close: '.finder-close' }
  ];
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function bindDialog(def) {
    const el = document.querySelector(def.sel);
    if (!el) return;
    const openerBtn = def.opener ? document.querySelector(def.opener) : null;
    let lastActive = null;
    let wasOpen = el.classList.contains('is-open');

    function focusables() {
      return Array.prototype.filter.call(el.querySelectorAll(FOCUSABLE), n => n.offsetParent !== null || n === document.activeElement);
    }

    new MutationObserver(() => {
      const open = el.classList.contains('is-open');
      if (open && !wasOpen) {
        lastActive = document.activeElement;
        if (openerBtn) openerBtn.setAttribute('aria-expanded', 'true');
        // Focus the dialog itself, not a field: that avoids raising the phone
        // keyboard, and the next Tab lands on the close button.
        el.setAttribute('tabindex', '-1');
        setTimeout(() => { try { el.focus({ preventScroll: true }); } catch (_) { el.focus(); } }, 30);
      } else if (!open && wasOpen) {
        if (openerBtn) openerBtn.setAttribute('aria-expanded', 'false');
        const back = lastActive && document.contains(lastActive) ? lastActive : openerBtn;
        lastActive = null;
        if (back && back.focus) back.focus({ preventScroll: true });
      }
      wasOpen = open;
    }).observe(el, { attributes: true, attributeFilter: ['class'] });

    el.addEventListener('keydown', e => {
      if (!el.classList.contains('is-open')) return;
      if (e.key === 'Escape') {
        const c = el.querySelector(def.close);
        if (c) { e.stopPropagation(); c.click(); }
        return;
      }
      if (e.key !== 'Tab') return;
      const f = focusables();
      if (!f.length) return;
      const first = f[0], last = f[f.length - 1];
      if (e.shiftKey && (document.activeElement === first || document.activeElement === el)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
  }

  function init() { DIALOGS.forEach(bindDialog); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();

// ── Size finder ──────────
// A three-question recommendation for 6mm or 8mm. The logic is deliberately
// plain and shown to the shopper: each answer points to 6mm, 8mm or neither,
// and the size with more answers wins. If it is even, the first answer (how
// they want them to look) decides. If nobody has any preference at all the
// suggestion is 8mm, and the result says that is the reason. Nothing here
// looks at price. Questions and copy live in the snippet's JSON block
// (snippets/size-finder.liquid) so this file stays generic.
(function SizeFinder() {
  const reduce = () => window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function recommend(cfg, answers) {
    const picks = cfg.questions.map((q, i) => (answers[i] == null ? null : q.options[answers[i]].pick));
    const n6 = picks.filter(p => p === cfg.sizes[0]).length;
    const n8 = picks.filter(p => p === cfg.sizes[1]).length;
    let size, basis;
    if (n6 !== n8) { size = n6 > n8 ? cfg.sizes[0] : cfg.sizes[1]; basis = 'majority'; }
    else if (picks[0]) { size = picks[0]; basis = 'first'; }
    else { size = cfg.sizes[1]; basis = 'default'; }
    return { size, basis, n6, n8, picks };
  }

  function bind(root) {
    const cfg = JSON.parse(root.querySelector('[data-finder-config]').textContent);
    const stage = root.querySelector('[data-finder-stage]');
    const stepEl = root.querySelector('[data-finder-step]');
    const bars = root.querySelectorAll('[data-finder-bar]');
    const answers = [];
    let step = 0;

    function setProgress(done) {
      // done < 0 is the size guide itself, which has no progress to show.
      const quiz = done >= 0;
      const barWrap = bars.length ? bars[0].parentElement : null;
      if (barWrap) barWrap.hidden = !quiz;
      if (stepEl) stepEl.hidden = !quiz;
      bars.forEach((b, i) => b.classList.toggle('is-done', i < done));
      if (stepEl && quiz) stepEl.textContent = done >= cfg.questions.length ? 'Your result' : 'Question ' + (done + 1) + ' of ' + cfg.questions.length;
    }
    function focusHeading() {
      const h = stage.querySelector('[data-finder-heading]');
      if (h) { try { h.focus({ preventScroll: true }); } catch (_) { h.focus(); } }
    }
    function swap(html, done) {
      stage.classList.remove('is-in');
      stage.innerHTML = html;
      setProgress(done);
      // Next frame so the fade-in transition runs; reduced motion skips it in CSS.
      requestAnimationFrame(() => stage.classList.add('is-in'));
      focusHeading();
    }
    function esc(t) { return String(t).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])); }

    // The size guide: both sizes side by side (a real photo of each when one is
    // set, plus a scale drawing), what each looks like, and a button for each.
    // The quiz is one tap away for anyone still unsure.
    function renderGuide() {
      const cards = cfg.guide.sizes.map(g => '<div class="guide-size">'
        + (g.image ? '<img class="guide-size__img" src="' + esc(g.image) + '" alt="' + esc(g.size) + ' SnapMags earring" loading="lazy" width="500" height="500">' : '')
        + '<span class="guide-size__dot" aria-hidden="true" style="width:' + g.dia + 'px;height:' + g.dia + 'px"></span>'
        + '<h3 class="guide-size__name">' + esc(g.size) + '</h3><p class="guide-size__line">' + esc(g.line) + '</p>'
        + '<button type="button" class="btn-primary guide-size__cta" data-finder-choose="' + esc(g.size) + '">Choose ' + esc(g.size) + '</button></div>').join('');
      swap('<p class="finder__why" tabindex="-1" data-finder-heading>' + esc(cfg.guide.intro) + '</p>'
        + '<div class="guide-sizes">' + cards + '</div>'
        + '<p class="guide-note">Sizes are shown relative to each other, not at actual size.</p>'
        + '<button type="button" class="btn-secondary finder__cta" data-finder-start>Not sure? Help me choose</button>', -1);
    }
    function renderQuestion() {
      const q = cfg.questions[step];
      const opts = q.options.map((o, i) => '<button type="button" class="finder-option' + (answers[step] === i ? ' is-picked' : '') + '" data-finder-answer="' + i + '"><span class="finder-option__label">' + esc(o.label) + '</span>' + (o.hint ? '<span class="finder-option__hint">' + esc(o.hint) + '</span>' : '') + '</button>').join('');
      swap('<h3 class="finder__question" id="finder-q-' + step + '" tabindex="-1" data-finder-heading>' + esc(q.title) + '</h3><div class="finder-options" role="group" aria-labelledby="finder-q-' + step + '">' + opts + '</div>' + '<button type="button" class="finder__back" data-finder-back>Back</button>', step);
    }
    function renderResult() {
      const r = recommend(cfg, answers);
      const other = r.size === cfg.sizes[0] ? cfg.sizes[1] : cfg.sizes[0];
      const reasons = cfg.questions.map((q, i) => (answers[i] != null && r.picks[i] === r.size) ? esc(q.options[answers[i]].label) : null).filter(Boolean);
      let why;
      if (r.basis === 'majority') why = (r.size === cfg.sizes[0] ? r.n6 : r.n8) + ' of ' + cfg.questions.length + ' answers pointed to ' + r.size + (reasons.length ? ' (' + reasons.join(', ').toLowerCase() + ')' : '') + '.';
      else if (r.basis === 'first') why = 'Your answers were evenly split, so we went with how you said you want them to look first.';
      else why = 'You had no strong preference, so we started with ' + r.size + ', the size that reads most like a standard stud.';
      const dia = { [cfg.sizes[0]]: 36, [cfg.sizes[1]]: 48 };
      swap(
        '<p class="finder__kicker">We suggest</p>' +
        '<h3 class="finder__result" tabindex="-1" data-finder-heading>' + esc(r.size) + '</h3>' +
        '<p class="finder__why">' + esc(cfg.copy[r.size]) + '</p>' +
        '<p class="finder__why finder__why--muted">' + why + '</p>' +
        '<div class="finder-scale" aria-hidden="true"><span class="finder-scale__dot' + (r.size === cfg.sizes[0] ? ' is-rec' : '') + '" style="width:' + dia[cfg.sizes[0]] + 'px;height:' + dia[cfg.sizes[0]] + 'px"></span><span class="finder-scale__dot' + (r.size === cfg.sizes[1] ? ' is-rec' : '') + '" style="width:' + dia[cfg.sizes[1]] + 'px;height:' + dia[cfg.sizes[1]] + 'px"></span></div>' +
        '<p class="finder-scale__cap" aria-hidden="true">' + esc(cfg.sizes[0]) + ' and ' + esc(cfg.sizes[1]) + ', relative sizes</p>' +
        '<div class="finder__actions"><button type="button" class="btn-primary finder__cta" data-finder-choose="' + esc(r.size) + '">Choose ' + esc(r.size) + '</button>' +
        '<button type="button" class="btn-secondary finder__cta" data-finder-choose="' + esc(other) + '">Choose ' + esc(other) + ' instead</button></div>' +
        '<button type="button" class="finder__back" data-finder-restart>Start over</button>' +
        '<details class="finder__how"><summary>How we chose</summary><p>' + esc(cfg.copy.how) + '</p></details>',
        cfg.questions.length
      );
    }
    function choose(size) {
      if (window.SnapMagsPDP) {
        const ok = window.SnapMagsPDP.setOption('Size', size);
        const modal = document.getElementById('size-finder-modal');
        if (modal) closeModal(modal);
        if (ok) {
          Toast.show('Size ' + size + ' selected. You can change it below.', 4200);
          const target = document.querySelector('[data-variant-pickers]') || document.getElementById('buy-box');
          if (target) target.scrollIntoView({ behavior: reduce() ? 'auto' : 'smooth', block: 'center' });
        } else {
          Toast.show(size + ' is not available with your current picks. Change another option to see it.', 4200);
        }
      } else {
        window.location.href = cfg.productUrl + '?size=' + encodeURIComponent(size) + '&from=finder#buy-box';
      }
    }
    root.addEventListener('click', e => {
      const a = e.target.closest('[data-finder-answer]');
      if (a) {
        answers[step] = parseInt(a.dataset.finderAnswer, 10);
        a.classList.add('is-picked');
        const next = () => { step++; if (step >= cfg.questions.length) renderResult(); else renderQuestion(); };
        if (reduce()) next(); else setTimeout(next, 220);
        return;
      }
      if (e.target.closest('[data-finder-start]')) { answers.length = 0; step = 0; renderQuestion(); return; }
      if (e.target.closest('[data-finder-back]')) { if (step === 0) { renderGuide(); } else { step -= 1; renderQuestion(); } return; }
      if (e.target.closest('[data-finder-restart]')) { answers.length = 0; step = 0; renderQuestion(); return; }
      const c = e.target.closest('[data-finder-choose]');
      if (c) choose(c.dataset.finderChoose);
    });
    root._finderReset = () => { answers.length = 0; step = 0; renderGuide(); };
    renderGuide();
  }

  function openModal(modal) {
    if (modal.classList.contains('is-open')) return;
    const root = modal.querySelector('[data-size-finder]');
    if (root && root._finderReset && modal.dataset.done === '1') { root._finderReset(); modal.dataset.done = ''; }
    modal.classList.add('is-open');
    lockBodyScroll();
  }
  function closeModal(modal) {
    if (!modal.classList.contains('is-open')) return;
    modal.classList.remove('is-open');
    unlockBodyScroll();
  }

  function init() {
    document.querySelectorAll('[data-size-finder]').forEach(bind);
    const modal = document.getElementById('size-finder-modal');
    document.addEventListener('click', e => {
      const opener = e.target.closest('[data-size-finder-open]');
      if (opener) {
        if (modal) { e.preventDefault(); openModal(modal); return; }
        const inline = document.getElementById('size-finder');
        if (inline) { e.preventDefault(); inline.scrollIntoView({ behavior: reduce() ? 'auto' : 'smooth', block: 'start' }); }
        return;
      }
      if (modal && e.target.closest('[data-finder-close]') && modal.contains(e.target)) closeModal(modal);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();
